import React, { useEffect, useState, useMemo, useRef } from 'react';
import { leerBolsaNoAlcanza, etiquetaDeContinuar } from '../lib/bolsaNoAlcanza.js';
import { cantidadEquipoPorUnidad } from '@supernova/shared';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Badge,
  Switch,
  Spinner,
  MessageBar,
  MessageBarBody,
  Input,
  Select,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  Dismiss16Regular,
  Add16Regular,
  Document16Regular,
  Info16Regular,
  Open16Regular,
} from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { Chip } from '../components/Chip.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import { MonedaObraCtx, formatoDeMoneda, useMonedaObra } from '../lib/money.js';
import { getProject } from '../api/projects.js';
import { ApiClientError } from '../api/client.js';
import { useAuthStore } from '../stores/authStore.js';
import { useQuoteJobsStore } from '../stores/quoteJobsStore.js';
import { useUsageStore, refreshUsageSoon } from '../stores/usageStore.js';
import { enCreditos } from '../lib/creditos.js';
import { OCULTAR_COSTO_IA } from '../lib/flags.js';
import {
  getApuIaRun,
  listApuIaRuns,
  getObraContext,
  generateApuIa,
  saveMatrixEdit,
  searchQuoteCatalog,
  type MatrixEditPayload,
  type CatalogPick,
  type CatalogSearchHit,
  approveAllMatrices,
  approveMatrix,
  regenerateConcept,
  recomputeConcept,
  rejectMatrix,
  reopenMatrix,
  resolveAuxiliarReceta,
  type ApuIaConcepto,
  type ApuIaRun,
  type ApuIaInsumo,
  type ObraContext,
} from '../api/apuIa.js';

// =============================================================
// Estilos — adaptación del prototipo opus_ai_prototype.html a Fluent v9
// =============================================================
const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    background: 'var(--bg-page)',
  },
  container: { maxWidth: '1440px', margin: '0 auto', padding: '20px 24px' },

  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: '16px',
  },
  breadcrumbLink: { color: tokens.colorBrandForeground1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } },

  // Sticky bar del concepto. AppTopBar global es sticky con altura --topbar-h (64px)
  // y z-index 100 — nos posicionamos JUSTO DEBAJO de ella.
  stickyBar: {
    position: 'sticky',
    top: 'var(--topbar-h)',
    zIndex: 90,
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '10px 24px',
    margin: '-20px -28px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
  },
  stickyTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  stickyKey: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    marginRight: '8px',
  },

  // ===== LISTA =====
  projectHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  projectTitle: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold, marginBottom: '4px' },
  projectSubtitle: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },

  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1px',
    background: 'var(--border-subtle)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    marginBottom: '18px',
  },
  statCard: {
    background: 'var(--bg-surface)',
    padding: '15px 17px',
  },
  statLabel: { fontSize: '10.5px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.7px', fontWeight: 650 },
  statValue: { fontSize: 'var(--fs-600)', fontWeight: 700, margin: '5px 0 2px', letterSpacing: '-0.5px', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' },
  statMeta: { fontSize: '11.5px', color: 'var(--text-secondary)' },
  covPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '22px',
    width: 'fit-content',
    padding: '0 9px',
    borderRadius: '999px',
    fontSize: '11.5px',
    fontWeight: 650,
    whiteSpace: 'nowrap',
  },
  covDot: { width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 },

  filtersBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 0',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  filterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '999px',
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    transition: 'all 0.15s',
    '&:hover': { background: tokens.colorNeutralBackground2 },
  },
  filterChipActive: {
    background: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
  },
  filterChipCount: {
    fontSize: tokens.fontSizeBase100,
    padding: '1px 7px',
    borderRadius: '999px',
    background: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  filterChipDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },

  matricesTable: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  matricesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  matricesHeaderTitle: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold },

  matrixRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 84px 90px 120px 260px',
    alignItems: 'start',
    padding: '10px 14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '12px',
    columnGap: '12px',
    cursor: 'pointer',
    transition: 'background 0.1s',
    '&:hover': { background: tokens.colorNeutralBackground2 },
    '&:last-child': { borderBottom: 'none' },
  },
  matrixRowExpanded: {
    background: 'var(--accent-weak)',
    boxShadow: 'inset 3px 0 0 var(--accent)',
    '&:hover': { background: 'var(--accent-weak)' },
  },
  detailInline: {
    // El panel expandido usaba --bg-raised, byte-idéntico al hover de fila
    // (colorNeutralBackground2) → se confundían. Usamos el fondo de página
    // (recesado) + riel de acento para que lea claramente como "abierto".
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: 'var(--bg-page)',
    boxShadow: 'inset 3px 0 0 var(--accent)',
    cursor: 'default',
  },
  // ===== Detalle estilo mockup (composición | condiciones) =====
  apuInner: {
    padding: '18px 22px',
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr',
    gap: '22px',
    '@media (max-width: 1100px)': { gridTemplateColumns: '1fr' },
  },
  apuBlockH5: {
    margin: '0 0 9px',
    fontSize: '11px',
    letterSpacing: '0.7px',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    fontWeight: 700,
  },
  secLab: {
    fontSize: '10px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    margin: '15px 0 2px',
    fontWeight: 650,
  },
  ins: { width: '100%', borderCollapse: 'collapse', marginTop: '3px' },
  insTh: {
    fontSize: '9px',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    fontWeight: 650,
    padding: '5px 8px',
    textAlign: 'right',
    borderBottom: '1px solid var(--border-subtle)',
  },
  insThFirst: { textAlign: 'left' },
  insTd: {
    fontSize: '12px',
    padding: '6px 8px',
    textAlign: 'right',
    borderBottom: '1px dashed var(--border-subtle)',
    color: 'var(--text-secondary)',
    verticalAlign: 'middle',
  },
  insTdFirst: { textAlign: 'left' },
  insImp: { fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-primary)' },
  insSub: {
    borderTop: '1px solid var(--border-strong)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    paddingTop: '9px',
  },
  insNote: {
    fontSize: '11px',
    padding: '2px 8px 6px',
    color: 'var(--text-tertiary)',
  },
  insCellInput: {
    width: '76px',
    minWidth: '76px',
  },
  origenBadge: {
    display: 'inline-block',
    fontSize: '9.5px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    padding: '1px 6px',
    borderRadius: '999px',
  },
  origenCat: { background: 'var(--oficial-weak)', color: 'var(--oficial)' },
  origenIa: { background: 'var(--privado-weak)', color: 'var(--privado)' },
  origenMkt: { background: 'var(--warn-weak)', color: 'var(--warn)', cursor: 'help' },
  condIntro: { fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 6px', lineHeight: 1.45 },
  condRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '9px 0',
    borderBottom: '1px dashed var(--border-subtle)',
  },
  condOff: { opacity: 0.45 },
  condMeta: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '12.5px', color: 'var(--text-secondary)' },
  scopeChip: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    padding: '2px 6px',
    borderRadius: '999px',
    background: 'var(--bg-overlay)',
    color: 'var(--text-tertiary)',
  },
  condEff: { color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', fontSize: '11.5px' },
  sw: {
    width: '34px',
    height: '20px',
    borderRadius: '99px',
    background: 'var(--bg-overlay)',
    border: '1px solid var(--border-strong)',
    position: 'relative',
    cursor: 'pointer',
    flexShrink: 0,
    transitionDuration: '0.15s',
    transitionProperty: 'background, border-color',
  },
  swOn: { background: 'var(--accent)', border: '1px solid var(--accent)' },
  swKnob: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: 'var(--text-tertiary)',
    transitionDuration: '0.15s',
    transitionProperty: 'transform, background',
  },
  swKnobOn: { transform: 'translateX(14px)', background: '#fff' },
  apuRollup: { marginTop: '14px', paddingTop: '6px', borderTop: '1px solid var(--border-subtle)' },
  apuLine: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12.5px',
    padding: '6px 0',
    borderBottom: '1px dashed var(--border-subtle)',
  },
  apuLineLast: { borderBottom: 'none' },
  apuLineD: { color: 'var(--text-secondary)' },
  apuLineA: { fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' },
  apuTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginTop: '14px',
    paddingTop: '13px',
    borderTop: '1px solid var(--border-strong)',
  },
  apuPu: { fontSize: '21px', fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' },
  apuActions: { display: 'flex', gap: '9px', marginTop: '14px', flexWrap: 'wrap' },
  // Botón "Cómo lo pensó OPUS ai": debe llamar la atención (gradiente + glow).
  razonBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '6px 14px',
    borderRadius: '999px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12.5px',
    fontWeight: 700,
    color: '#fff',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    background: 'linear-gradient(120deg, var(--accent), var(--privado))',
    boxShadow: '0 2px 12px rgba(108,84,214,0.35)',
    transition: 'transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 5px 18px rgba(108,84,214,0.55)', filter: 'brightness(1.06)' },
    '&:active': { transform: 'translateY(0)' },
  },
  razonBtnSpark: { fontSize: '13px', filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.6))' },
  ajusteWrap: { marginTop: '18px', paddingTop: '12px', borderTop: '1px dashed var(--border-subtle)' },
  ajusteToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 650,
    fontFamily: 'inherit',
    color: 'var(--text-secondary)',
    '&:hover': { color: 'var(--text-primary)' },
  },
  ajusteCount: {
    fontSize: '10px',
    fontWeight: 700,
    minWidth: '16px',
    textAlign: 'center',
    padding: '1px 6px',
    borderRadius: '999px',
    background: 'var(--bg-overlay)',
    color: 'var(--text-tertiary)',
  },
  apuBelow: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '16px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  matrixRowHead: {
    background: tokens.colorNeutralBackground2,
    fontSize: '10.5px',
    fontWeight: 650,
    color: 'var(--text-tertiary)',
    padding: '11px 14px',
    cursor: 'default',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    '&:hover': { background: tokens.colorNeutralBackground2 },
  },
  matrixKey: { fontWeight: tokens.fontWeightRegular, lineHeight: '1.45', color: 'var(--text-primary)' },
  matrixDesc: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, marginTop: '2px' },

  // ===== ANÁLISIS =====
  analysisCard: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
  },
  analysisHeader: {
    padding: '18px 22px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  analysisKicker: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.04em' },
  analysisTitle: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, marginTop: '4px' },
  analysisSubtitle: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: '4px' },
  analysisActions: { display: 'flex', gap: '8px', flexShrink: 0 },

  defaultsBar: {
    background: tokens.colorNeutralBackground2,
    padding: '12px 22px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  defaultsInfo: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
  defaultsDivider: { width: '1px', height: '12px', background: tokens.colorNeutralStroke2 },
  defaultsName: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold, display: 'inline-flex', alignItems: 'center', gap: '6px' },
  defaultsEdit: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: tokens.fontSizeBase200,
    '&:hover': { textDecoration: 'underline' },
  },

  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 360px',
    '@media (max-width: 1100px)': { gridTemplateColumns: '1fr' },
  },
  matrixArea: {
    padding: '20px 22px',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  sidePanel: {
    padding: '20px 18px',
    background: tokens.colorNeutralBackground2,
  },

  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '10px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  sectionSubtotal: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },

  dataTable: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    marginBottom: '22px',
  },
  dataTableHead: {
    background: tokens.colorNeutralBackground2,
    padding: '8px 14px',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    display: 'grid',
    gap: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    // Forzar que cada celda del grid pueda achicarse por debajo del min-content
    // (Fluent Input tiene min-width ~150px que rompe el sizing 'fr' del grid).
    '> *': { minWidth: 0 },
  },
  dataTableRow: {
    padding: '11px 14px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase300,
    display: 'grid',
    gap: '8px',
    alignItems: 'center',
    '> *': { minWidth: 0 },
  },
  // Inputs en celdas de grid: forzar ocupar todo el ancho de la celda.
  // Fluent Input tiene min-width default ~150px que rompe el sizing 'fr'.
  cellInput: {
    width: '100%',
    minWidth: 0,
  },
  auxRow: {
    background: tokens.colorPaletteLavenderBackground2,
  },
  auxDetail: {
    padding: '8px 14px 8px 30px',
    fontSize: tokens.fontSizeBase200,
    background: tokens.colorPaletteLavenderBackground2,
    color: tokens.colorNeutralForeground2,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  // Más espacio a la descripción (4fr) — los inputs numéricos son cortos.
  gridMat: { gridTemplateColumns: '4fr 0.8fr 0.7fr 0.5fr 0.7fr 0.8fr 0.3fr' },
  gridEq: { gridTemplateColumns: '4fr 0.8fr 0.7fr' },
  gridEqFull: { gridTemplateColumns: '4fr 0.9fr 0.7fr 0.8fr 0.3fr' },
  amount: { textAlign: 'right', fontWeight: tokens.fontWeightSemibold, fontVariantNumeric: 'tabular-nums' },
  insumoNameCell: {
    minWidth: 0,
    overflow: 'hidden',
  },
  insumoName: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowAction: {
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    textAlign: 'right',
    '&:hover': { color: tokens.colorPaletteRedForeground1 },
  },
  auxLinkRow: { padding: '0 14px 8px 14px', background: tokens.colorNeutralBackground1 },
  auxLink: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    '&:hover': { textDecoration: 'underline' },
  },
  dataTableFoot: {
    padding: '10px 14px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase200,
    display: 'flex',
    gap: '16px',
  },
  footLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    '&:hover': { textDecoration: 'underline' },
  },
  priceLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: tokens.fontSizeBase300,
    '&:hover': { textDecoration: 'underline' },
  },

  moBlock: {
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '14px 16px',
    marginBottom: '22px',
  },
  moGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '18px',
    fontSize: tokens.fontSizeBase200,
  },
  moGrid3: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '18px',
    fontSize: tokens.fontSizeBase200,
  },
  moLabel: { color: tokens.colorNeutralForeground3, marginBottom: '2px', fontSize: tokens.fontSizeBase200 },
  moValue: { fontSize: tokens.fontSizeBase300 },

  toolBlock: {
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '22px',
  },
  toolLeft: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: tokens.fontSizeBase300 },

  sideField: { marginBottom: '10px', fontSize: tokens.fontSizeBase200 },
  sideFieldLabel: { color: tokens.colorNeutralForeground3, marginBottom: '4px', fontSize: tokens.fontSizeBase100 },
  sideFieldValue: { color: tokens.colorNeutralForeground1 },

  factorsCard: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '14px 16px',
  },
  factorsKicker: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  factorLine: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: tokens.fontSizeBase200,
    marginBottom: '8px',
    gap: '8px',
  },
  factorName: { color: tokens.colorNeutralForeground2 },
  factorEvidencia: { color: tokens.colorNeutralForeground3, fontStyle: 'italic', marginLeft: '4px' },
  factorConf: { color: tokens.colorPaletteDarkOrangeForeground1, marginLeft: '4px', fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
  factorsMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    paddingTop: '8px',
    marginTop: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  reasoningBlock: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '18px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  bottomPanel: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '18px 22px',
    background: tokens.colorNeutralBackground1,
    display: 'grid',
    gridTemplateColumns: '1.2fr 1.5fr 0.9fr',
    gap: '24px',
    alignItems: 'center',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  cdLabel: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3 },
  cdValue: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold, lineHeight: 1.1, marginTop: '4px' },
  cdUnit: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightRegular, marginLeft: '6px' },
  cdBreakdown: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, marginTop: '6px' },

  vLabel: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', justifyContent: 'space-between' },
  closeBlock: { textAlign: 'right' },
  impValue: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, marginBottom: '10px' },
  vItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: tokens.fontSizeBase200, marginBottom: '3px' },
  vSuccess: { color: tokens.colorPaletteGreenForeground1 },
  vWarning: { color: tokens.colorPaletteDarkOrangeForeground1 },
  vDanger: { color: tokens.colorPaletteRedForeground1 },

  memoryBox: {
    background: tokens.colorNeutralBackground2,
    padding: '10px 14px',
    borderRadius: tokens.borderRadiusSmall,
    marginTop: '12px',
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground2,
  },
});

// =============================================================
// Componente principal
// =============================================================
export function QuoteApuIaRunPage() {
  const styles = useStyles();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { id: projectId, qid: quoteId, runId } = useParams<{ id: string; qid: string; runId?: string }>();
  const tenant = useAuthStore((s) => s.tenant);
  const isStaff = !!tenant?.isEcosoftOwned && (tenant.role === 'admin' || tenant.role === 'owner');

  const [run, setRun] = useState<ApuIaRun | null>(null);
  const [conceptos, setConceptos] = useState<ApuIaConcepto[]>([]);
  // Conceptos marcados con el toggle "Cotizar con IA" que aún no tienen APU.
  const [pendingApuIa, setPendingApuIa] = useState(0);
  const [obraContext, setObraContext] = useState<ObraContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [noRuns, setNoRuns] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [obraChecked, setObraChecked] = useState(false);

  // Carga obra-context (no bloquea si falla)
  useEffect(() => {
    if (!projectId) return;
    getObraContext(projectId)
      .then((r) => { setObraContext(r.context); setObraChecked(true); })
      .catch(() => { setObraContext(null); setObraChecked(true); });
  }, [projectId]);

  // Nombre del proyecto para el breadcrumb.
  const [projectName, setProjectName] = useState<string | null>(null);
  // La moneda de la obra: la misma con la que se decidieron los precios. Sin ella
  // los importes se muestran sin símbolo, nunca en pesos mexicanos por default.
  const [monedaObra, setMonedaObra] = useState<string | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  // Láminas de planos que todavía no se leyeron: se avisa antes de generar.
  const [planosPorLeer, setPlanosPorLeer] = useState(0);
  useEffect(() => {
    if (!projectId) return;
    getProject(projectId)
      .then((r) => {
        setProjectName(r.project.name);
        setMonedaObra(r.project.moneda ?? null);
        setIsDemo(r.project.isExample === true);
        setPlanosPorLeer(r.project.leerPlanos === false ? 0 : (r.planosPendientes ?? 0));
      })
      .catch(() => {});
  }, [projectId]);

  // Carga el run y sus conceptos
  useEffect(() => {
    if (!quoteId) return;
    let cancelled = false;
    setNoRuns(false);

    async function load() {
      try {
        let activeRunId = runId;
        if (!activeRunId) {
          const list = await listApuIaRuns(quoteId!);
          if (cancelled) return;
          activeRunId = list.runs[0]?.id;
          if (!activeRunId) {
            setNoRuns(true);
            setLoading(false);
            return;
          }
        }
        const detail = await getApuIaRun(quoteId!, activeRunId);
        if (cancelled) return;
        setRun(detail.run);
        setConceptos(detail.conceptos);
      setPendingApuIa(detail.pendingApuIa ?? 0);
        setLoading(false);

        // Registra/actualiza el run en el indicador global del topbar. Con el
        // job en el store, el watcher de App abre el WS del proyecto y recibe
        // el progreso fino (apu_ia:progress) aunque el usuario navegue.
        if (projectId) {
          const active = detail.run.status === 'queued' || detail.run.status === 'running';
          if (active) {
            useQuoteJobsStore.getState().upsert({
              kind: 'apu_ia', quoteId: quoteId!, projectId,
              stage: detail.run.status, status: 'running',
              total: detail.run.conceptos_total || undefined,
            });
          } else if (detail.run.status === 'completed') {
            // Terminó OK: se marca TERMINADO y además se descarta el aviso — el usuario ya
            // está en esta sub-página con los resultados, así que el pill "listos" del
            // topbar sobra (el de progreso ya cumplió mientras se navegaba fuera).
            //
            // Marcarlo terminado no es cosmético: descartar el aviso NO cambiaba el estado,
            // así que el trabajo quedaba "en curso" para siempre en la pantalla. El
            // presupuesto, que se fía de eso para saber si hay algo corriendo, se quedaba
            // recargándose solo cada 3 segundos sobre una hoja quieta y con los controles
            // bloqueados hasta recargar el navegador (reporte de Sebastián, 2026-08-12).
            useQuoteJobsStore.getState().upsert({
              kind: 'apu_ia', quoteId: quoteId!, projectId,
              stage: 'done', status: 'done', pct: 100,
              total: detail.run.conceptos_total || undefined,
              processed: detail.run.conceptos_total || undefined,
            });
            useQuoteJobsStore.getState().dismiss(`apu_ia:${quoteId!}`);
            refreshUsageSoon(); // el APU consumió tokens → refrescar el pill del header
          } else {
            useQuoteJobsStore.getState().upsert({
              kind: 'apu_ia', quoteId: quoteId!, projectId,
              stage: detail.run.status, status: 'failed',
              total: detail.run.conceptos_total || undefined,
            });
          }
        }

        if (detail.run.status === 'queued' || detail.run.status === 'running') {
          setPolling(true);
          setTimeout(() => { if (!cancelled) void load(); }, 2000);
        } else {
          setPolling(false);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiClientError ? e.message : 'Error al cargar el run');
        setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [quoteId, runId]);

  // Recarga manual de conceptos tras "Ajustar" (recompute no crea run, así que
  // el polling de runs no lo refresca solo).
  async function reloadConceptos(): Promise<ApuIaConcepto[] | null> {
    if (!quoteId || !run) return null;
    try {
      const detail = await getApuIaRun(quoteId, run.id);
      setRun(detail.run);
      setConceptos(detail.conceptos);
      setPendingApuIa(detail.pendingApuIa ?? 0);
      return detail.conceptos;
    } catch { return null; }
  }

  // Detalle inline (acordeón): al expandir un concepto, lleva su fila al
  // viewport sin saltar al tope (la lista permanece visible).
  useEffect(() => {
    if (selectedId) {
      const el = document.getElementById(`apu-row-${selectedId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedId]);

  // Genera (o regenera) los análisis con IA de la cotización. Requiere contexto
  // de obra para aplicar los factores. En planes topados puede responder
  // QUOTA_EXCEEDED → ofrecemos generar los primeros N.
  async function onGenerate() {
    if (!quoteId || !projectId) return;
    // Contexto de proyecto requerido para aplicar factores de rendimiento.
    try {
      await getObraContext(projectId);
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 404) {
        if (await confirm({
          title: 'Falta el contexto de proyecto',
          message:
            'Este proyecto no tiene contexto de proyecto capturado. El análisis de precios unitarios lo necesita para aplicar ' +
            'los factores correctamente.\n\n¿Ir a configurarlo ahora?',
          confirmLabel: 'Configurar',
        })) navigate(`/project/${projectId}/obra-context`, { state: { returnTo: window.location.pathname } });
        return;
      }
      // Otro error al leer el contexto: dejamos que el backend valide.
    }
    // Bolsa única de tokens mensual: los APUs ya no tienen límite por counter
    // (limits.apus_generated viene null). Si hubiera un límite, se informa.
    const usage = useUsageStore.getState().data;
    const apusLimit = usage?.limits.apus_generated ?? null;
    const apusRemaining = apusLimit == null
      ? null
      : Math.max(0, apusLimit - (usage?.counters.apus_generated ?? 0));
    // Aviso de planos: si quedan láminas sin leer, se leen ANTES de generar y eso
    // consume tokens. Callarlo sería cobrarle sin avisarle.
    const avisoPlanos = planosPorLeer > 0
      ? `Este proyecto tiene ${planosPorLeer} hoja(s) de planos sin leer. ` +
        'Se van a leer antes de generar los análisis, para tomar de ahí medidas y ' +
        'cantidades que el listado de conceptos no dice. Eso consume ' + (enCreditos(usage) ? 'créditos' : 'tokens') + ' de tu ' +
        'bolsa una sola vez: después queda guardado y no se vuelven a leer.\n\n' +
        'Si prefieres que no los lea, apaga "Leer los planos" en la pestaña ' +
        'Documentos del proyecto.\n\n'
      : '';
    if (!(await confirm({
      title: 'Generar análisis de precios unitarios',
      message:
        avisoPlanos +
        'Se generará el análisis de precio unitario con inteligencia artificial de los conceptos marcados que aún no lo tienen. ' +
        'Los que ya están generados no se tocan.\n\n' +
        'Para volver a crear uno existente usa "Regenerar" en el concepto.\n\n' +
        (apusRemaining != null ? `Cuota de APUs restante este mes: ${apusRemaining.toLocaleString('es-MX')}.` : ''),
      confirmLabel: 'Continuar',
    }))) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await generateApuIa(quoteId);
      navigate(`/project/${projectId}/quotes/${quoteId}/apu-ia/runs/${res.runId}`);
    } catch (e) {
      // La bolsa no alcanza para todo el lote. El servidor NO encoló nada: dijo cuántos
      // entran y espera respuesta. Es el aviso que no existía cuando un cliente gastó dos
      // veces y media su plan en una sola hoja sin que nadie se lo dijera.
      const noAlcanza = leerBolsaNoAlcanza(e);
      if (noAlcanza) {
        if (await confirm({
          title: 'Tu bolsa no alcanza para todo',
          message: noAlcanza.mensaje,
          confirmLabel: etiquetaDeContinuar(noAlcanza, 'análisis'),
        })) {
          try {
            const res3 = await generateApuIa(quoteId, { confirmarParcial: true });
            navigate(`/project/${projectId}/quotes/${quoteId}/apu-ia/runs/${res3.runId}`);
            return;
          } catch (e3) {
            setError(e3 instanceof ApiClientError ? e3.message : 'Error al iniciar el análisis de precios unitarios');
          }
        }
        return;
      }
      if (e instanceof ApiClientError && e.code === 'QUOTA_EXCEEDED') {
        const d = (e.details ?? {}) as { current?: number; limit?: number | null; effectiveLimit?: number | null };
        const cap = d.effectiveLimit ?? d.limit ?? 0;
        const remaining = Math.max(0, (cap ?? 0) - (d.current ?? 0));
        if (remaining > 0 && (await confirm({
          title: 'Cuota de análisis insuficiente',
          message: `Tu cuota permite generar ${remaining} análisis de precio unitario más este mes.\n\n¿Generar los primeros ${remaining} ahora?`,
          confirmLabel: `Generar ${remaining}`,
        }))) {
          try {
            const res2 = await generateApuIa(quoteId, { maxConcepts: remaining });
            navigate(`/project/${projectId}/quotes/${quoteId}/apu-ia/runs/${res2.runId}`);
            return;
          } catch (e2) {
            setError(e2 instanceof ApiClientError ? e2.message : 'Error al iniciar el análisis de precios unitarios');
          }
        }
      } else {
        setError(e instanceof ApiClientError ? e.message : 'Error al iniciar el análisis de precios unitarios');
      }
    } finally {
      setGenerating(false);
    }
  }

  // Breadcrumb: Inicio / Proyectos / {proyecto} / Cotización (link) / APUs (actual).
  const apuCrumbs = [
    { label: 'Inicio', to: '/projects' },
    { label: 'Proyectos', to: '/projects' },
    { label: projectName ?? 'Proyecto', to: `/project/${projectId}` },
    { label: 'Presupuesto', to: `/project/${projectId}/quotes/${quoteId}` },
    { label: 'Análisis de precios unitarios con IA' },
  ];

  if (loading) {
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={apuCrumbs} />
        <div className={styles.container}><Spinner label="Cargando análisis de precios unitarios…" /></div>
      </div>
    );
  }

  if (noRuns) {
    const contextMissing = obraChecked && !obraContext;
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={apuCrumbs} />
        <div className={styles.container}>
          <Button icon={<ArrowLeft24Regular />} appearance="subtle" onClick={() => navigate(`/project/${projectId}/quotes/${quoteId}`)}>
            Volver al presupuesto
          </Button>
          {error && (
            <MessageBar intent="error" style={{ marginTop: 16 }}>
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}
          {contextMissing && (
            <MessageBar intent="warning" style={{ marginTop: 16 }}>
              <MessageBarBody>
                Este proyecto no tiene <strong>contexto de proyecto</strong> capturado. El análisis de
                precios unitarios lo necesita para aplicar los factores de rendimiento; sin él los
                precios pueden salir imprecisos.{' '}
                <Button size="small" appearance="transparent" onClick={() => navigate(`/project/${projectId}/obra-context`, { state: { returnTo: window.location.pathname } })}>
                  Capturar contexto de proyecto
                </Button>
              </MessageBarBody>
            </MessageBar>
          )}
          <div style={{ marginTop: 24, textAlign: 'center', padding: '48px 20px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⚡</div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Aún no creaste análisis de precios unitarios con IA</h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: 540, margin: '0 auto 20px', lineHeight: 1.5 }}>
              OPUS construye con inteligencia artificial el análisis de precio unitario (cuadrilla,
              materiales y equipo) de los conceptos marcados. Por defecto vienen marcados los que no
              encontraron equivalente en catálogo; marca más en el presupuesto con el toggle de la
              columna <strong>Analizar con IA</strong>.
            </p>
            <Button
              appearance="primary"
              disabled={generating || isDemo}
              icon={generating ? <Spinner size="extra-tiny" /> : undefined}
              onClick={() => void onGenerate()}
            >
              {generating ? 'Iniciando…' : '⚡ Crear análisis de precios unitarios con IA'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={apuCrumbs} />
        <div className={styles.container}>
          <Button icon={<ArrowLeft24Regular />} appearance="subtle" onClick={() => navigate(`/project/${projectId}/quotes/${quoteId}`)}>
            Volver al presupuesto
          </Button>
          <MessageBar intent="warning" style={{ marginTop: 16 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
      </div>
    );
  }

  return (
    // La moneda de la obra baja por contexto: la usan el listado, el detalle y la
    // composición de los auxiliares sin pasarla por cinco niveles de props.
    <MonedaObraCtx.Provider value={formatoDeMoneda(monedaObra)}>
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={apuCrumbs} />
      <div className={styles.container}>

        {polling && (
          <div className={styles.breadcrumb}>
            <Badge appearance="filled" color="brand" style={{ marginLeft: 'auto' }}>
              Procesando…
            </Badge>
          </div>
        )}

        {run && (run.status === 'queued' || run.status === 'running')
          ? <RunProgressView run={run} conceptos={conceptos} isStaff={isStaff} />
          : (() => {
              // Handler común para aprobar/rechazar/reabrir desde lista o detalle.
              const handleReview = async (conceptId: string, action: 'approve' | 'reject' | 'reopen') => {
                if (isDemo) return; // proyecto de ejemplo: solo lectura, no se aprueba/rechaza
                try {
                  const fn = action === 'approve' ? approveMatrix : action === 'reject' ? rejectMatrix : reopenMatrix;
                  const res = await fn(conceptId);
                  setConceptos((prev) => prev.map((c) =>
                    c.id === conceptId
                      ? { ...c, apu_ia_review_status: res.status as ApuIaConcepto['apu_ia_review_status'], apu_ia_reviewed_at: new Date().toISOString() }
                      : c,
                  ));
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Error al actualizar estado');
                }
              };
              // Detalle inline (acordeón): la fila seleccionada expande el
              // análisis completo sin abandonar la lista (igual que el mockup).
              const renderDetail = (sc: ApuIaConcepto) => (
                <AnalysisView
                  concepto={sc}
                  obraContext={obraContext}
                  styles={styles}
                  isStaff={isStaff}
                  onBack={() => setSelectedId(null)}
                  onEditContext={() => navigate(`/project/${projectId}/obra-context`, { state: { returnTo: window.location.pathname } })}
                  onReviewAction={(action) => handleReview(sc.id, action)}
                  onRecompute={async (overrides) => {
                    // El recálculo es un job async; sondeamos hasta que la BD
                    // refleje el ajuste (cada factor del override con su dispara),
                    // así nunca mostramos precio viejo. Máx ~8s.
                    const reflejado = (cs: ApuIaConcepto[] | null) => {
                      const c = cs?.find((x) => x.id === sc.id);
                      if (!c) return false;
                      const fa = (c.apu_ia_factores_aplicados ?? []) as Array<{ id: string; dispara?: boolean }>;
                      return overrides.every((o) => {
                        const f = fa.find((x) => x.id === o.id);
                        return f ? Boolean(f.dispara) === o.activo : true;
                      });
                    };
                    try {
                      await recomputeConcept(sc.id, overrides);
                      for (let i = 0; i < 12; i++) {
                        await new Promise((r) => setTimeout(r, 700));
                        if (reflejado(await reloadConceptos())) break;
                      }
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Error al recalcular');
                    }
                  }}
                  onRegenerate={async () => {
                    try {
                      const res = await regenerateConcept(sc.id);
                      // Marcar local como pendiente (UI feedback rápido).
                      // El worker actualiza la BD; el polling de runs trae el nuevo state.
                      setConceptos((prev) => prev.map((c) =>
                        c.id === sc.id
                          ? { ...c, apu_ia_matrix: null, apu_ia_review_status: null, apu_ia_confianza_semaforo: null }
                          : c,
                      ));
                      setError(`↻ Regenerando ${res.clave ?? 'concepto'}…`);
                      setSelectedId(null);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Error al regenerar concepto');
                    }
                  }}
                  onSaveEdit={async (edit) => {
                    // Edición manual (2026-07-03): job async — sondear hasta que la
                    // BD refleje la nueva traza editado_manual (mismo patrón que
                    // onRecompute) para no mostrar composición/estado viejos.
                    const prevAt = sc.apu_ia_matrix?.editado_manual?.at ?? null;
                    const reflejado = (cs: ApuIaConcepto[] | null) => {
                      const c = cs?.find((x) => x.id === sc.id);
                      return !!c && (c.apu_ia_matrix?.editado_manual?.at ?? null) !== prevAt;
                    };
                    try {
                      await saveMatrixEdit(sc.id, edit);
                      for (let i = 0; i < 12; i++) {
                        await new Promise((r) => setTimeout(r, 700));
                        if (reflejado(await reloadConceptos())) break;
                      }
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Error al guardar la edición');
                      throw err; // que el botón no apague dirty si falló
                    }
                  }}
                />
              );
              return <ListView
                    run={run}
                    conceptos={conceptos}
                    styles={styles}
                    isStaff={isStaff}
                    quoteId={quoteId ?? ''}
                    expandedId={selectedId}
                    renderDetail={renderDetail}
                    onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
                    onEditContext={() => navigate(`/project/${projectId}/obra-context`, { state: { returnTo: window.location.pathname } })}
                    onReviewAction={handleReview}
                    onApproveAll={async () => {
                      if (!quoteId || isDemo) return;
                      try {
                        const res = await approveAllMatrices(quoteId);
                        setConceptos((prev) => prev.map((c) =>
                          c.apu_ia_matrix && (c.apu_ia_review_status === 'pending' || c.apu_ia_review_status == null)
                            ? { ...c, apu_ia_review_status: 'approved' as const, apu_ia_reviewed_at: new Date().toISOString() }
                            : c,
                        ));
                        setError(`✓ ${res.approved} matrices aprobadas`);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Error al aprobar todas');
                      }
                    }}
                    pendingApuIa={pendingApuIa}
                    generating={generating}
                    isDemo={isDemo}
                    onGenerateNew={() => void onGenerate()}
                  />;
            })()
        }
      </div>
    </div>
    </MonedaObraCtx.Provider>
  );
}

// =============================================================
// Vista de progreso del run (mientras está queued/running)
// =============================================================

function RunProgressView({ run, conceptos, isStaff }: { run: ApuIaRun; conceptos: ApuIaConcepto[]; isStaff: boolean }) {
  const conceptosTotal = run.conceptos_total || 1;
  const procesados = (run.conceptos_ok || 0) + (run.conceptos_failed || 0);
  // conceptos en BD con apu_ia_matrix != null — feedback más en vivo entre actualizaciones del run row
  const realProcesados = conceptos.filter((c) => c.apu_ia_matrix != null || c.apu_ia_confianza_semaforo != null).length;
  const visible = Math.max(procesados, realProcesados);
  const pct = Math.min(100, Math.round((visible / conceptosTotal) * 100));

  const elapsedSec = run.triggered_at
    ? Math.floor((Date.now() - new Date(run.triggered_at).getTime()) / 1000)
    : 0;
  const eta = visible > 0 && visible < conceptosTotal
    ? Math.round((elapsedSec / visible) * (conceptosTotal - visible))
    : null;

  const lastDone = useMemo(() => {
    const done = conceptos.filter((c) => c.apu_ia_matrix != null);
    return done[done.length - 1] ?? null;
  }, [conceptos]);

  const tips = [
    'OPUS ai armando insumos, cuadrillas y rendimientos…',
    'Buscando precios en los catálogos del proyecto…',
    'Aplicando factores del contexto de proyecto…',
    'Resolviendo auxiliares y sub-matrices…',
    'Calculando subtotales y costo directo…',
  ];
  const tipIdx = Math.floor(elapsedSec / 6) % tips.length;

  return (
    <div style={{
      background: tokens.colorNeutralBackground1,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      borderRadius: tokens.borderRadiusLarge,
      padding: '48px 32px',
      textAlign: 'center',
    }}>
      {/* Header */}
      <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {run.status === 'queued' ? '⏳ En cola' : '✨ Procesando con OPUS ai'}
      </div>
      <h1 style={{ fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold, marginTop: 8, marginBottom: 4 }}>
        Creando {conceptosTotal} análisis de precios unitarios con IA
      </h1>
      <div style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, marginBottom: 28 }}>
        {tips[tipIdx]}
      </div>

      {/* Anillo de progreso grande */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <div style={{ position: 'relative', width: 140, height: 140 }}>
          <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="70" cy="70" r="60" fill="none" stroke={tokens.colorNeutralStroke2} strokeWidth="10" />
            <circle
              cx="70"
              cy="70"
              r="60"
              fill="none"
              stroke={tokens.colorBrandForeground1}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${(pct / 100) * 377} 377`}
              style={{ transition: 'stroke-dasharray 0.4s ease' }}
            />
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column',
          }}>
            <div style={{ fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold }}>{pct}%</div>
            <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>{visible} / {conceptosTotal}</div>
          </div>
        </div>
      </div>

      {/* Stats en vivo */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12,
        maxWidth: 720,
        margin: '0 auto 24px',
      }}>
        <StatTile label="Procesados OK" value={String(run.conceptos_ok ?? 0)} color={tokens.colorPaletteGreenForeground1} />
        <StatTile label="Pendientes" value={String(Math.max(0, conceptosTotal - visible))} />
        <StatTile label="Transcurrido" value={`${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, '0')}`} />
        {eta != null && eta > 0 && (
          <StatTile label="Tiempo restante" value={`~${Math.floor(eta / 60)}:${String(eta % 60).padStart(2, '0')}`} />
        )}
        {/* Tokens y Costo: solo Ecosoft staff */}
        {isStaff && (
          <>
            <StatTile label="Tokens IA · staff" value={`${(run.tokens_input_total + run.tokens_output_total).toLocaleString()}`} />
            {!OCULTAR_COSTO_IA && <StatTile label="Costo USD · staff" value={`$${Number(run.cost_usd_total).toFixed(4)}`} />}
          </>
        )}
      </div>

      {/* Último concepto procesado (sensación de movimiento) */}
      {lastDone && (
        <div style={{
          background: tokens.colorNeutralBackground2,
          border: `1px solid ${tokens.colorNeutralStroke2}`,
          borderRadius: tokens.borderRadiusMedium,
          padding: '10px 16px',
          maxWidth: 600,
          margin: '0 auto',
          textAlign: 'left',
          fontSize: tokens.fontSizeBase200,
        }}>
          <div style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase100, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            ✓ Último procesado
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {lastDone.clave && (
              <code style={{ color: tokens.colorBrandForeground1, fontSize: tokens.fontSizeBase200 }}>{lastDone.clave}</code>
            )}
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {lastDone.descripcion}
            </span>
          </div>
        </div>
      )}

      <div style={{ marginTop: 28, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
        Esta página se actualiza sola. Puedes dejarla abierta o volver más tarde — la generación sigue en segundo plano.
      </div>
    </div>
  );
}

function StatTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      background: tokens.colorNeutralBackground2,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      borderRadius: tokens.borderRadiusMedium,
      padding: '12px 14px',
    }}>
      <div style={{
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground3,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>{label}</div>
      <div style={{
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        marginTop: 2,
        fontVariantNumeric: 'tabular-nums',
        color: color ?? tokens.colorNeutralForeground1,
      }}>{value}</div>
    </div>
  );
}

// =============================================================
// Vista 1 — Listado de matrices
// =============================================================

type ListFilter = 'all' | 'gris' | 'catalogo_completo' | 'con_mercado' | 'reviewed' | 'outdated';

/**
 * Cuántos insumos de un análisis salieron con precio del catálogo del cliente.
 *
 * Es un HECHO, no un veredicto. El semáforo (`apu_ia_confianza_semaforo`) se sigue
 * calculando y guardando igual que siempre —lo usa la auditoría de precios de staff—
 * pero al cotizador ya no se le pinta un color de alarma: se le dice el número y él
 * saca la conclusión, que además es la correcta porque él sabe si en su obra eso
 * importa. Pedido de Sebastián (2026-08-14): "rojo indica como que hay algo mal, en
 * realidad hay que hacer notar que simplemente hay recursos que se buscaron su precio
 * en internet".
 *
 * Se lee `apu_ia_atomicos_match` (insumos resueltos contra catálogo) sobre
 * `apu_ia_atomicos_total`, NO `_missing`: tras una edición manual el missing puede
 * quedar corto (un renglón 'manual' con precio no cae ni en match ni en missing),
 * mientras que match/total siempre son comparables.
 */
function insumosDeCatalogo(c: ApuIaConcepto): { total: number; match: number } | null {
  const total = c.apu_ia_atomicos_total ?? 0;
  if (c.apu_ia_matrix == null || total <= 0) return null;
  const match = Math.max(0, Math.min(total, c.apu_ia_atomicos_match ?? 0));
  return { total, match };
}

/** El análisis tomó TODOS sus insumos del catálogo del cliente. */
function todoDeCatalogo(c: ApuIaConcepto): boolean {
  const i = insumosDeCatalogo(c);
  return !!i && i.match >= i.total;
}

/** Al análisis le faltó algún insumo en el catálogo → ese tomó precio de mercado. */
function conPrecioDeMercado(c: ApuIaConcepto): boolean {
  const i = insumosDeCatalogo(c);
  return !!i && i.match < i.total;
}

function ListView({
  run, conceptos, styles, isStaff, quoteId, expandedId, renderDetail, onSelect, onEditContext, onReviewAction, onApproveAll,
  pendingApuIa, generating, isDemo, onGenerateNew,
}: {
  run: ApuIaRun | null;
  conceptos: ApuIaConcepto[];
  styles: ReturnType<typeof useStyles>;
  isStaff: boolean;
  quoteId: string;
  expandedId: string | null;
  renderDetail: (c: ApuIaConcepto) => React.ReactNode;
  onSelect: (id: string) => void;
  onEditContext: () => void;
  onReviewAction: (conceptId: string, action: 'approve' | 'reject' | 'reopen') => Promise<void>;
  onApproveAll: () => Promise<void>;
  /** Conceptos marcados con el toggle "Cotizar con IA" que aún no tienen APU. */
  pendingApuIa: number;
  generating: boolean;
  isDemo: boolean;
  /** Genera en lote los marcados pendientes (flujo iterativo). */
  onGenerateNew: () => void;
}) {
  void quoteId; // usado por el handler del padre (onApproveAll)
  // Importes en la moneda de la obra (viene del contexto que monta la página).
  const fmtMonedaObra = useMonedaObra();
  const { id: printProjectId, qid: printQuoteId, runId: printRunId } = useParams<{ id: string; qid: string; runId?: string }>();
  const hasSinteticos = conceptos.some((c) => c.apu_ia_matrix != null);
  const openPrint = (mode: 'simple' | 'detallado') => {
    if (!printProjectId || !printQuoteId || !printRunId) return;
    window.open(`/project/${printProjectId}/quotes/${printQuoteId}/apu-ia/runs/${printRunId}/print?mode=${mode}`, '_blank', 'noopener');
  };
  const [filter, setFilter] = useState<ListFilter>('all');
  const [search, setSearch] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [confirmApproveAll, setConfirmApproveAll] = useState(false);

  // "Pendientes de aceptar" = lo que el cotizador todavía NO aceptó ni rechazó
  // (incluye las que aún no se generaron). El estado que importa es la ACEPTACIÓN
  // de la matriz, no la cobertura del catálogo.
  const esGris = (c: ApuIaConcepto) =>
    c.apu_ia_review_status !== 'approved' && c.apu_ia_review_status !== 'rejected';
  // Candidatos a la aceptación en bloque: los que tomaron TODOS sus insumos del
  // catálogo. Antes el criterio era el semáforo verde, que ya no se muestra; ahora
  // es exactamente el dato que se lee en la fila, así que el botón nunca promete un
  // recorte que la pantalla no deje ver.
  const esCompletoPendiente = (c: ApuIaConcepto) =>
    todoDeCatalogo(c) && c.apu_ia_review_status !== 'approved';

  const counts = useMemo(() => {
    const cobs = conceptos
      .map(insumosDeCatalogo)
      .filter((i): i is { total: number; match: number } => i != null);
    return {
      all: conceptos.length,
      gris: conceptos.filter(esGris).length,
      catalogoCompleto: conceptos.filter(todoDeCatalogo).length,
      conMercado: conceptos.filter(conPrecioDeMercado).length,
      completoPendiente: conceptos.filter(esCompletoPendiente).length,
      insumosTotal: cobs.reduce((s, i) => s + i.total, 0),
      insumosMatch: cobs.reduce((s, i) => s + i.match, 0),
      reviewed: conceptos.filter((c) => c.apu_ia_review_status === 'approved').length,
      outdated: conceptos.filter((c) => c.apu_ia_context_outdated).length,
    };
  }, [conceptos]);

  // Orden: primero lo que todavía no tiene análisis, después de menor a mayor
  // proporción de insumos tomados del catálogo. Antes ordenaba por el semáforo
  // (rojo → amarillo → verde); ahora por el mismo dato que se ve en la fila, para
  // que el orden se pueda explicar mirándola.
  const ordenCatalogo = (c: ApuIaConcepto) => {
    if (c.apu_ia_matrix == null) return -1;
    const i = insumosDeCatalogo(c);
    return i ? i.match / i.total : 2;
  };

  const filteredConceptos = useMemo(() => {
    let result = conceptos;
    // Filtro por estado de aceptación / origen de los precios
    if (filter === 'gris') result = result.filter(esGris);
    else if (filter === 'reviewed') result = result.filter((c) => c.apu_ia_review_status === 'approved');
    else if (filter === 'outdated') result = result.filter((c) => c.apu_ia_context_outdated);
    else if (filter === 'catalogo_completo') result = result.filter(todoDeCatalogo);
    else if (filter === 'con_mercado') result = result.filter(conPrecioDeMercado);
    // Filtro por búsqueda de texto
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter((c) =>
        (c.clave?.toLowerCase().includes(q) ?? false) ||
        c.descripcion.toLowerCase().includes(q),
      );
    }
    // Orden por origen de los precios (menos catálogo, más arriba). El sort de JS es
    // estable, así que los empates conservan el orden original (por índice).
    return [...result].sort((a, b) => ordenCatalogo(a) - ordenCatalogo(b));
  }, [conceptos, filter, search]);

  const closedCount = conceptos.filter((c) => c.apu_ia_matrix != null).length;
  const failedCount = conceptos.filter((c) => c.apu_ia_matrix == null).length;
  const totalAlertas = conceptos.reduce((s, c) => s + (c.apu_ia_alertas?.length ?? 0), 0);

  return (
    <>
      <div className={styles.projectHeader}>
        <div>
          <div className={styles.projectTitle}>Análisis de precios unitarios con IA</div>
          <div className={styles.projectSubtitle}>
            Generación del {run ? new Date(run.triggered_at).toLocaleString('es-MX') : '—'} ·
            Estado:{' '}
            <Badge size="small" appearance="filled" color={run?.status === 'completed' ? 'success' : run?.status === 'failed' ? 'danger' : 'warning'}>
              {run?.status === 'completed' ? 'completada' : run?.status === 'failed' ? 'falló' : run?.status === 'running' ? 'procesando' : run?.status === 'queued' ? 'en cola' : '—'}
            </Badge>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasSinteticos && (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button>🖨 Imprimir ▾</Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => openPrint('simple')}>
                    <div>
                      <b>Impresión simple</b>
                      <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>Conceptos y su análisis por tipo de recurso.</div>
                    </div>
                  </MenuItem>
                  <MenuItem onClick={() => openPrint('detallado')}>
                    <div>
                      <b>Impresión detallada</b>
                      <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>Incluye memoria de cálculo, factores, razonamientos y validaciones.</div>
                    </div>
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
          <Button onClick={onEditContext}>Editar contexto de proyecto</Button>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Matrices generadas</div>
          <div className={styles.statValue}>{run?.conceptos_ok ?? 0}</div>
          <div className={styles.statMeta}>{closedCount} con análisis · {failedCount} sin análisis</div>
        </div>
        {/* Antes acá vivía "Requieren atención" = amarillos + rojos del semáforo, en
            naranja. Comunicaba "hay algo mal" cuando el hecho es neutro: unos insumos
            salieron del catálogo del cliente y otros tomaron precio de mercado. Ahora
            se informa el dato; el juicio lo hace el cotizador. */}
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Insumos con precio de tu catálogo</div>
          <div className={styles.statValue}>
            {counts.insumosTotal > 0
              ? `${counts.insumosMatch.toLocaleString('es-MX')} de ${counts.insumosTotal.toLocaleString('es-MX')}`
              : '—'}
          </div>
          <div className={styles.statMeta}>
            {counts.insumosTotal > 0
              ? `${counts.catalogoCompleto} de ${counts.catalogoCompleto + counts.conMercado} análisis salieron completos de tu catálogo`
              : 'Todavía no hay insumos analizados'}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Alertas</div>
          <div className={styles.statValue}>{totalAlertas}</div>
          <div className={styles.statMeta}>Revisar antes de cerrar</div>
        </div>
        {/* Tokens y Costo: solo Ecosoft staff */}
        {isStaff && run && (
          <>
            <div className={styles.statCard} style={{ background: 'rgba(93,163,245,0.06)', borderColor: 'rgba(93,163,245,0.3)' }}>
              <div className={styles.statLabel}>Tokens IA · staff</div>
              <div className={styles.statValue} style={{ fontSize: tokens.fontSizeBase400 }}>
                {run.tokens_input_total.toLocaleString()} / {run.tokens_output_total.toLocaleString()}
              </div>
              <div className={styles.statMeta}>OPUS ai (reasoning)</div>
            </div>
            {!OCULTAR_COSTO_IA && (
              <div className={styles.statCard} style={{ background: 'rgba(93,163,245,0.06)', borderColor: 'rgba(93,163,245,0.3)' }}>
                <div className={styles.statLabel}>Costo USD · staff</div>
                <div className={styles.statValue}>${Number(run.cost_usd_total).toFixed(4)}</div>
                <div className={styles.statMeta}>Solo Ecosoft</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* El matiz, dicho una sola vez y sin alarma: el precio de mercado no es un
          error, pero tampoco se defiende igual que uno del catálogo del cliente. */}
      {counts.conMercado > 0 && (
        <div style={{
          fontSize: tokens.fontSizeBase200,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          margin: '-6px 0 16px',
          maxWidth: 860,
        }}>
          Los insumos que no están en tus catálogos tomaron precio de mercado. Sirven para
          tener el análisis completo, aunque frente a tu cliente un precio de tu catálogo
          se sostiene mejor: ahí decides si conviene revisarlos.
        </div>
      )}

      {/* Barra de búsqueda */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'center' }}>
        <Input
          size="small"
          placeholder="🔎 Buscar por clave o descripción del concepto…"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ flex: 1, maxWidth: 480 }}
        />
        {search.trim() && (
          <Button size="small" appearance="subtle" onClick={() => setSearch('')}>
            ✕ Limpiar
          </Button>
        )}
        {search.trim() && (
          <span style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
            {filteredConceptos.length} de {conceptos.length}
          </span>
        )}
      </div>

      {/* Barra de filtros por estado de aceptación de la matriz */}
      <div className={styles.filtersBar}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, flex: '1 1 auto' }}>
          <FilterChip label="Ver todas" count={counts.all} active={filter === 'all'} onClick={() => setFilter('all')} />
          <FilterChip label="Pendientes de aceptar" count={counts.gris} active={filter === 'gris'} onClick={() => setFilter('gris')} icon="●" />
          <FilterChip label="Aceptadas" count={counts.reviewed} active={filter === 'reviewed'} onClick={() => setFilter('reviewed')} icon="✓" />
          {/* Filtros por el ORIGEN de los precios (hecho), no por el semáforo (veredicto).
              El punto de color es solo para leer de un vistazo: verde = todo de tu
              catálogo, azul de acento = hay precios de mercado. Nunca rojo de error. */}
          {counts.catalogoCompleto > 0 && (
            <FilterChip
              label="Todo de tu catálogo"
              count={counts.catalogoCompleto}
              active={filter === 'catalogo_completo'}
              onClick={() => setFilter('catalogo_completo')}
              dotColor="var(--ok)"
            />
          )}
          {counts.conMercado > 0 && (
            <FilterChip
              label="Con precios de mercado"
              count={counts.conMercado}
              active={filter === 'con_mercado'}
              onClick={() => setFilter('con_mercado')}
              dotColor="var(--accent)"
            />
          )}
          {counts.outdated > 0 && (
            <FilterChip label="Desactualizadas" count={counts.outdated} active={filter === 'outdated'} onClick={() => setFilter('outdated')} icon="⚠" />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <Button
            size="small"
            appearance="primary"
            disabled={generating || isDemo || pendingApuIa === 0}
            icon={generating ? <Spinner size="extra-tiny" /> : undefined}
            title={pendingApuIa === 0
              ? 'Marca conceptos en el presupuesto con el toggle para generarlos con inteligencia artificial.'
              : 'Generar el análisis con inteligencia artificial de los conceptos marcados en el presupuesto que aún no lo tienen.'}
            onClick={() => onGenerateNew()}
            style={pendingApuIa === 0 ? undefined : { background: 'var(--privado)', borderColor: 'var(--privado)' }}
          >
            {generating ? 'Iniciando…' : `⚡ Generar nuevos${pendingApuIa > 0 ? ` (${pendingApuIa})` : ''}`}
          </Button>
          {/* Aceptar en lote, OCULTO tras `MOSTRAR_ACEPTAR_EN_LOTE` (ver el flag).
              Antes decía "Aceptar alta confianza" y tomaba los del semáforo verde; al
              salir el semáforo de la pantalla quedaba prometiendo un recorte invisible,
              así que quedó apuntado a los que la fila muestra completos de catálogo. Si
              se reactiva, eso es lo que acepta. */}
          {MOSTRAR_ACEPTAR_EN_LOTE && counts.completoPendiente > 0 && (
            <Button
              size="small"
              appearance="primary"
              disabled={accepting || isDemo}
              title="Acepta de una vez los análisis que tomaron todos sus insumos de tu catálogo. Los que tienen precios de mercado los revisas abajo, uno por uno."
              onClick={async () => {
                setAccepting(true);
                try {
                  for (const c of conceptos.filter(esCompletoPendiente)) {
                    await onReviewAction(c.id, 'approve');
                  }
                } finally {
                  setAccepting(false);
                }
              }}
            >
              {accepting ? 'Aceptando…' : `✓ Aceptar los de tu catálogo (${counts.completoPendiente})`}
            </Button>
          )}
          {isStaff && (() => {
            const pending = conceptos.filter((c) =>
              c.apu_ia_matrix && (c.apu_ia_review_status === 'pending' || c.apu_ia_review_status == null),
            ).length;
            if (pending === 0) return null;
            return (
              <>
                <Button
                  size="small"
                  appearance="outline"
                  title="Aprueba en bulk todas las matrices pending. Solo visible para staff Ecosoft (smoke tests m0→m1)."
                  onClick={() => setConfirmApproveAll(true)}
                >
                  ✓ Aprobar todas ({pending})
                </Button>
                <Dialog open={confirmApproveAll} onOpenChange={(_, d) => { if (!d.open) setConfirmApproveAll(false); }}>
                  <DialogSurface style={{ maxWidth: 480 }}>
                    <DialogBody>
                      <DialogTitle>Aprobar todas las matrices</DialogTitle>
                      <DialogContent style={{ fontSize: 13, lineHeight: 1.6 }}>
                        Se aceptarán las <strong>{pending} matrices</strong> generadas por inteligencia artificial,
                        incluidas las de cobertura parcial o limitada. Podrás reabrir cualquiera después.
                      </DialogContent>
                      <DialogActions>
                        <Button appearance="secondary" onClick={() => setConfirmApproveAll(false)}>Cancelar</Button>
                        <Button appearance="primary" onClick={async () => { setConfirmApproveAll(false); await onApproveAll(); }}>
                          Aprobar todas
                        </Button>
                      </DialogActions>
                    </DialogBody>
                  </DialogSurface>
                </Dialog>
              </>
            );
          })()}
        </div>
      </div>

      <div className={styles.matricesTable}>
        <div className={styles.matricesHeader}>
          <div className={styles.matricesHeaderTitle}>
            Matrices generadas · click para ver análisis
            {filter !== 'all' && <span style={{ marginLeft: 8, color: tokens.colorNeutralForeground3 }}>· filtrado: {filteredConceptos.length} de {conceptos.length}</span>}
          </div>
          <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
            {counts.reviewed > 0
              ? <>✓ <strong style={{ color: tokens.colorPaletteGreenForeground1 }}>{counts.reviewed}</strong> matriz(ces) lista(s) — disponibles al importar desde OPUS</>
              : <>Aprueba matrices para que estén disponibles al importar desde OPUS</>}
          </div>
        </div>
        <div className={`${styles.matrixRow} ${styles.matrixRowHead}`}>
          <div>Concepto</div>
          <div>Unidad</div>
          <div style={{ textAlign: 'right' }}>Cantidad</div>
          <div style={{ textAlign: 'right', paddingRight: 14 }}>Importe</div>
          <div>Estado</div>
        </div>
        {filteredConceptos.map((c) => {
          const matrix = c.apu_ia_matrix;
          const expanded = expandedId === c.id;
          const cob = insumosDeCatalogo(c);
          const completo = cob != null && cob.match >= cob.total;
          return (
            <React.Fragment key={c.id}>
            <div id={`apu-row-${c.id}`} className={`${styles.matrixRow}${expanded ? ` ${styles.matrixRowExpanded}` : ''}`} onClick={() => onSelect(c.id)}>
              <div>
                <div className={styles.matrixKey}>
                  {c.clave && <span style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: '12px', marginRight: 8 }}>{c.clave}</span>}
                  {c.descripcion}
                </div>
                {/* El HECHO, en lugar del color de alarma: cuántos insumos salieron del
                    catálogo del cliente. El punto de color es para leer de un vistazo,
                    no para juzgar — de ahí que el caso "hay precios de mercado" use el
                    azul de acento y no un rojo de error. */}
                {cob && (
                  <div
                    className={styles.matrixDesc}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    title={completo
                      ? 'Todos los insumos de este análisis tienen precio de tu catálogo.'
                      : `${cob.total - cob.match} insumo(s) no están en tus catálogos: tomaron precio de mercado.`}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                      background: completo ? 'var(--ok)' : 'var(--accent)',
                    }} />
                    {cob.match} de {cob.total} insumos con precio de tu catálogo
                  </div>
                )}
              </div>
              <div>{c.unidad}</div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.cantidad}</div>
              <div style={{ textAlign: 'right', paddingRight: 14, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {c.apu_ia_importe != null
                  ? fmtMonedaObra.money(Number(c.apu_ia_importe))
                  : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {!matrix && (
                  <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--text-tertiary)' }}>Sin generar</span>
                )}
                {c.apu_ia_review_status === 'approved' && (
                  <Badge size="small" appearance="filled" color="success" style={{ marginTop: 2 }}>✓ APROBADA</Badge>
                )}
                {c.apu_ia_review_status === 'rejected' && (
                  <Badge size="small" appearance="filled" color="danger" style={{ marginTop: 2 }}>✕ RECHAZADA</Badge>
                )}
                {c.apu_ia_context_outdated && (
                  <Badge size="small" appearance="filled" color="warning" style={{ marginTop: 2 }} title="El contexto del proyecto cambió después de generarse esta matriz. Regenera para actualizar.">
                    ⚠ DESACTUALIZADA
                  </Badge>
                )}
                {matrix && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                    {c.apu_ia_review_status !== 'approved' && (
                      <Button
                        size="small"
                        appearance="primary"
                        title="Aprobar matriz"
                        onClick={async (e) => { e.stopPropagation(); await onReviewAction(c.id, 'approve'); }}
                      >✓ Aceptar matriz</Button>
                    )}
                    {c.apu_ia_review_status !== 'rejected' && (
                      <Button
                        size="small"
                        appearance="outline"
                        title="Rechazar matriz"
                        onClick={async (e) => { e.stopPropagation(); await onReviewAction(c.id, 'reject'); }}
                      >✕ Rechazar</Button>
                    )}
                    {(c.apu_ia_review_status === 'approved' || c.apu_ia_review_status === 'rejected') && (
                      <Button
                        size="small"
                        appearance="subtle"
                        title="Reabrir revisión"
                        onClick={async (e) => { e.stopPropagation(); await onReviewAction(c.id, 'reopen'); }}
                      >↺ Reabrir</Button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {expanded && (
              <div className={styles.detailInline} onClick={(e) => e.stopPropagation()}>
                {renderDetail(c)}
              </div>
            )}
            </React.Fragment>
          );
        })}
        {filteredConceptos.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
            {filter === 'all'
              ? 'No hay conceptos procesados.'
              : filter === 'reviewed'
                ? 'Todavía no aceptaste ningún análisis.'
                : filter === 'catalogo_completo'
                  ? 'Ningún análisis tomó todos sus insumos de tu catálogo.'
                  : filter === 'con_mercado'
                    ? 'Ningún análisis tomó precios de mercado.'
                    : 'No hay análisis en este grupo.'}
          </div>
        )}
      </div>
    </>
  );
}

// =============================================================
// Vista 2 — Análisis detallado de un concepto
// =============================================================

interface EditMaterial {
  /** Índice del renglón en la matriz original; null = agregado a mano. */
  origIndex: number | null;
  /** Presente si el renglón nuevo se eligió del buscador de catálogos. */
  catalogPick?: CatalogPick;
  nombre: string;
  precio: number;
  cantidadNeta: number;
  desperdicio: number;
  unidad: string;
  esAuxiliar: boolean;
  claveCatalogo?: string;
  descCatalogo?: string;
  fuentePrecio?: string;
  /** Referencia de mercado (Serper): nota + presentación de dónde salió el precio. */
  notaMercado?: string;
  presentacionMercado?: string;
  /** P3 (c): true si el precio viene de un concepto terminado (tipo 7) que ya
   * incluye mano de obra → posible doble conteo con la cuadrilla. */
  incluyeMo?: boolean;
  /** true si el Analista lo añadió manualmente — habilita edición del nombre */
  _isNew?: boolean;
}

interface EditEquipo {
  /** Índice del renglón en la matriz original; null = agregado a mano. */
  origIndex: number | null;
  /** Presente si el renglón nuevo se eligió del buscador de catálogos. */
  catalogPick?: CatalogPick;
  nombre: string;
  costoHorario: number;
  horas: number;
  claveCatalogo?: string;
  fuentePrecio?: string;
  notaMercado?: string;
  presentacionMercado?: string;
  /** true si el Analista lo añadió manualmente — habilita edición del nombre */
  _isNew?: boolean;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
}


/**
 * Diálogo "agregar desde catálogo": búsqueda FTS ($0, sin IA) sobre los
 * catálogos de la cotización; elegir un recurso lo agrega como renglón con
 * fuente 'catalogo' (2026-07-03).
 */
function CatalogPickerDialog({ quoteId, kind, onPick, onClose }: {
  quoteId: string;
  kind: 'material' | 'equipo';
  onPick: (hit: CatalogSearchHit) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CatalogSearchHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function buscar() {
    const term = q.trim();
    if (term.length < 2) return;
    setBusy(true);
    setErr(null);
    try {
      setHits(await searchQuoteCatalog(quoteId, term, kind));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error al buscar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 640 }}>
        <DialogBody>
          <DialogTitle>Buscar {kind === 'material' ? 'insumo' : 'equipo'} en tus catálogos</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Input
                style={{ flex: 1 }}
                autoFocus
                placeholder="Ej. cemento gris, malla 8/8, revolvedora…"
                value={q}
                onChange={(_, d) => setQ(d.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void buscar(); }}
              />
              <Button appearance="primary" disabled={busy || q.trim().length < 2} onClick={() => void buscar()}>
                {busy ? 'Buscando…' : 'Buscar'}
              </Button>
            </div>
            {err && <MessageBar intent="error"><MessageBarBody>{err}</MessageBarBody></MessageBar>}
            {hits !== null && hits.length === 0 && !busy && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '8px 0' }}>
                Sin resultados en los catálogos de este presupuesto. Puedes agregarlo manualmente con "Añadir".
              </div>
            )}
            {hits && hits.length > 0 && (
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                {hits.map((h) => (
                  <div
                    key={`${h.catalogId}:${h.clave}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onPick(h)}
                    onKeyDown={(e) => { if (e.key === 'Enter') onPick(h); }}
                    style={{
                      display: 'flex', alignItems: 'baseline', gap: 10, padding: '8px 10px',
                      borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', borderRadius: 6,
                    }}
                    className="catalog-hit"
                  >
                    <code style={{ fontSize: 11.5, color: 'var(--accent)', flexShrink: 0 }}>{h.clave}</code>
                    <span style={{ flex: 1, fontSize: 13 }}>{h.descripcion}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>{h.unidad || '—'}</span>
                    <strong style={{ fontSize: 13, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {h.precio != null ? `$${fmt(h.precio)}` : 'sin precio'}
                    </strong>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Cerrar</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/**
 * Normaliza la cuadrilla a un array de {cantidad, categoria}.
 * Los APUs generados por LLM usan `componentes`; los de ECOSTOS/biblioteca
 * usan `recursos` con `descripcion` en vez de `categoria`.
 */
function getCuadrillaComps(c: unknown): Array<{ cantidad: number; categoria: string }> {
  if (!c || typeof c !== 'object') return [];
  const obj = c as Record<string, unknown>;
  const arr = Array.isArray(obj['componentes']) ? obj['componentes'] :
              Array.isArray(obj['recursos'])    ? obj['recursos']    : [];
  return (arr as unknown[]).map((x: unknown) => {
    const r = x as Record<string, unknown>;
    return {
      cantidad: Number(r['cantidad'] ?? r['jornadas'] ?? 0),
      categoria: String(r['categoria'] ?? r['descripcion'] ?? ''),
    };
  });
}

// Feature-flags de la vista del análisis con IA: ocultan UI manteniendo intacta
// toda la lógica (recálculo, pickers, handlers). Reactivar = poner en `true`.
//   AI-19 → "Ajustar condiciones de obra" (switches de factores).
//   AI-20 → edición manual de insumos ("Añadir insumo" / "Buscar en catálogos").
const MOSTRAR_AJUSTE_CONDICIONES = false;

// Aceptar en lote — OCULTO (2026-08-14, decisión de Sebastián): aprobar varios de una
// vez queda como herramienta de staff (el botón de "todas las matrices pending", que
// sigue arriba tras `isStaff`). El cotizador aprueba uno por uno, que es donde de
// verdad mira el precio. La lógica queda intacta: reactivar = poner en `true`.
const MOSTRAR_ACEPTAR_EN_LOTE = false;
const MOSTRAR_EDICION_INSUMOS = false;

function AnalysisView({
  concepto, obraContext, styles, isStaff, onBack, onReviewAction, onEditContext, onRegenerate, onRecompute, onSaveEdit,
}: {
  concepto: ApuIaConcepto;
  obraContext: ObraContext | null;
  styles: ReturnType<typeof useStyles>;
  isStaff: boolean;
  onBack: () => void;
  onReviewAction: (action: 'approve' | 'reject' | 'reopen') => Promise<void>;
  onEditContext: () => void;
  onRegenerate: () => Promise<void>;
  onRecompute: (overrides: { id: string; activo: boolean }[]) => Promise<void>;
  /** Edición manual persistente (2026-07-03): guarda composición y aprueba. */
  onSaveEdit: (edit: MatrixEditPayload) => Promise<void>;
}) {
  const confirm = useConfirm();
  const matrix = concepto.apu_ia_matrix;
  const factores = concepto.apu_ia_factores_aplicados ?? [];

  // Condiciones ("Ajustar"): el usuario apaga/prende factores → recálculo sin
  // LLM (D7). offIds = factores apagados; arranca con los que NO dispararon.
  const [offIds, setOffIds] = useState<Set<string>>(() => new Set(factores.filter((f) => !f.dispara).map((f) => f.id)));
  const [recomputing, setRecomputing] = useState(false);
  // "Ajustar condiciones de obra" arranca COLAPSADO: al abrir un APU el foco
  // es el precio; los switches de factores quedan ocultos hasta que el usuario
  // los despliega a propósito (2026-07-25).
  const [showAjuste, setShowAjuste] = useState(false);


  // ===== Estado editable =====
  // Unidad y cantidad NO son editables: vienen del concepto del catálogo origen.
  // Se exponen como const para que el resto del cálculo siga funcionando.
  const unidad = concepto.unidad;
  const cantidad = String(concepto.cantidad);
  const [materiales, setMateriales] = useState<EditMaterial[]>(() =>
    matrix?.materiales.map((m, idx) => {
      const enriched = m as typeof m & { precio_unitario?: number | null; clave_catalogo?: string; descripcion_catalogo?: string; fuente?: string; incluye_mo?: boolean; nota_mercado?: string; presentacion_mercado?: string };
      return {
        origIndex: idx,
        nombre: m.descripcion_normalizada,
        precio: enriched.precio_unitario ?? 0,
        cantidadNeta: m.cantidad_neta,
        desperdicio: m.desperdicio_pct ?? 0,
        unidad: m.unidad,
        esAuxiliar: m.tipo === 'auxiliar',
        claveCatalogo: enriched.clave_catalogo,
        descCatalogo: enriched.descripcion_catalogo,
        fuentePrecio: enriched.fuente,
        incluyeMo: enriched.incluye_mo === true,
        notaMercado: enriched.nota_mercado,
        presentacionMercado: enriched.presentacion_mercado,
      };
    }) ?? [],
  );
  const [equipos, setEquipos] = useState<EditEquipo[]>(() =>
    matrix?.equipo?.map((e, idx) => {
      const enriched = e as typeof e & { precio_unitario?: number | null; clave_catalogo?: string; descripcion_catalogo?: string; fuente?: string; nota_mercado?: string; presentacion_mercado?: string };
      return {
        origIndex: idx,
        // Fallback a la forma de ECOSTOS/biblioteca (`descripcion`/`cantidad`).
        nombre: e.descripcion_normalizada ?? e.descripcion ?? '',
        costoHorario: enriched.precio_unitario ?? 0,
        // Horas por UNIDAD del concepto (regla compartida): es la misma base en
        // la que se costea, así el editor y el importe coinciden.
        horas: cantidadEquipoPorUnidad(e, rendAdj).cantidad,
        claveCatalogo: enriched.clave_catalogo,
        fuentePrecio: enriched.fuente,
        notaMercado: enriched.nota_mercado,
        presentacionMercado: enriched.presentacion_mercado,
      };
    }) ?? [],
  );
  // Edición manual persistente (2026-07-03): cualquier mutación prende dirty;
  // "Guardar cambios" manda el PUT (el estado de revisión no cambia).
  const [editDirty, setEditDirty] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  // Picker "agregar desde catálogo" (FTS $0 sobre los catálogos de la cotización).
  const { qid: pickerQuoteId } = useParams<{ qid: string }>();
  const [pickerFor, setPickerFor] = useState<'material' | 'equipo' | null>(null);

  function onCatalogPicked(hit: CatalogSearchHit) {
    setEditDirty(true);
    const pick: CatalogPick = { catalog_id: hit.catalogId, clave_catalogo: hit.clave, descripcion_catalogo: hit.descripcion };
    if (pickerFor === 'material') {
      setMateriales((prev) => [...prev, {
        origIndex: null, catalogPick: pick, nombre: hit.descripcion, unidad: hit.unidad,
        cantidadNeta: 0, desperdicio: 0, precio: hit.precio ?? 0, esAuxiliar: false,
        claveCatalogo: hit.clave, fuentePrecio: 'catalogo', _isNew: true,
      }]);
    } else if (pickerFor === 'equipo') {
      setEquipos((prev) => [...prev, {
        origIndex: null, catalogPick: pick, nombre: hit.descripcion,
        costoHorario: hit.precio ?? 0, horas: 0, claveCatalogo: hit.clave, fuentePrecio: 'catalogo', _isNew: true,
      }]);
    }
    setPickerFor(null);
  }
  const [herramientaPct, setHerramientaPct] = useState(matrix?.herramienta_pct ?? 3);
  const [rendBase, setRendBase] = useState(concepto.apu_ia_rendimiento_base ?? matrix?.rendimiento_base ?? 0);
  const [expandedAux, setExpandedAux] = useState<Record<number, boolean>>({});
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [razonamientoOpen, setRazonamientoOpen] = useState(false);
  const [costoHorarioDialog, setCostoHorarioDialog] = useState<{ idx: number; nombre: string; precio: number } | null>(null);
  // ¿Hay algo que mostrar en "Cómo lo pensó OPUS ai"? (razonamiento + memoria de
  // decisiones + validaciones/alertas/preguntas). Si no, no se muestra el botón.
  const hasRazonamiento = !!(
    matrix?.razonamiento_alcance || matrix?.razonamiento_cuadrilla ||
    matrix?.razonamiento_equipo || matrix?.razonamiento_desperdicios ||
    matrix?.memoria_decisiones ||
    (concepto.apu_ia_validaciones?.length ?? 0) > 0 ||
    (concepto.apu_ia_alertas?.length ?? 0) > 0 ||
    (concepto.apu_ia_preguntas_pendientes?.length ?? 0) > 0
  );

  // ===== Cálculos =====
  const rendAdj = concepto.apu_ia_rendimiento_ajustado ?? rendBase;
  const subMat = useMemo(
    () => materiales.reduce((s, m) => s + (m.precio || 0) * (m.cantidadNeta || 0) * (1 + (m.desperdicio || 0) / 100), 0),
    [materiales],
  );
  // SR cuadrilla: lo resuelve el backend matcheando los componentes contra el
  // catálogo (apu_ia_sr_cuadrilla). Si por alguna razón no llegó, usamos 0 —
  // ya NO caemos a SR hardcoded de "1 oficial + 1 peón" como antes (era un
  // vestigio del mockup de dirección y resultaba engañoso cuando la cuadrilla
  // real de Grok no encajaba en esas 3 categorías predefinidas).
  const srEfectivo = concepto.apu_ia_sr_cuadrilla ?? 0;
  const subMo = rendAdj > 0 ? srEfectivo / rendAdj : 0;
  const subHer = subMo * (herramientaPct / 100);
  const subEq = useMemo(
    () => equipos.reduce((s, e) => s + (rendAdj > 0 ? (e.costoHorario * e.horas) / rendAdj : 0), 0),
    [equipos, rendAdj],
  );
  const cdTotal = subMat + subMo + subEq + subHer;
  const importe = cdTotal * (parseFloat(cantidad) || 0);

  function updateMaterial(i: number, k: keyof EditMaterial, v: string | number | boolean) {
    setEditDirty(true);
    setMateriales((prev) => prev.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  }
  function removeMaterial(i: number) { setEditDirty(true); setMateriales((prev) => prev.filter((_, idx) => idx !== i)); }
  function addMaterial() {
    setEditDirty(true);
    setMateriales((prev) => [...prev, { origIndex: null, nombre: '', precio: 0, cantidadNeta: 0, desperdicio: 0, unidad: '', esAuxiliar: false, _isNew: true }]);
  }
  function updateEquipo(i: number, k: keyof EditEquipo, v: string | number) {
    setEditDirty(true);
    setEquipos((prev) => prev.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  }
  function removeEquipo(i: number) { setEditDirty(true); setEquipos((prev) => prev.filter((_, idx) => idx !== i)); }
  function addEquipo() {
    setEditDirty(true);
    setEquipos((prev) => [...prev, { origIndex: null, nombre: '', costoHorario: 0, horas: 0, _isNew: true }]);
  }

  // Guardar la edición manual: aviso suave si hay insumos sin precio (pueden
  // aprobar un APU con $0 sin querer), arma el payload y delega al caller.
  async function onGuardarEdicion() {
    const sinPrecio =
      materiales.filter((m) => !m.esAuxiliar && (m.precio || 0) <= 0).length +
      equipos.filter((e) => (e.costoHorario || 0) <= 0).length;
    if (sinPrecio > 0 && !(await confirm({
      title: 'Insumos sin precio',
      message: `Hay ${sinPrecio} insumo(s) sin precio (quedarán en $0 y contarán como faltantes).\n\n¿Guardar de todas formas?`,
      confirmLabel: 'Guardar',
    }))) return;
    if (!(await confirm({
      title: 'Guardar composición editada',
      message:
        'Se guardará la composición editada (el estado de revisión no cambia — apruebas cuando termines).\n\n' +
        'Para volver a la versión de la inteligencia artificial tendrías que volver a crearlo (cuenta contra tu cuota). ¿Continuar?',
      confirmLabel: 'Continuar',
    }))) return;
    setSavingEdit(true);
    try {
      await onSaveEdit({
        materiales: materiales.map((m) => ({
          origIndex: m.origIndex,
          descripcion: m.nombre.trim() || '(sin descripción)',
          unidad: m.unidad ?? '',
          cantidad_neta: m.cantidadNeta || 0,
          desperdicio_pct: m.desperdicio || 0,
          precio_unitario: (m.precio || 0) > 0 ? m.precio : null,
          ...(m.origIndex == null && m.catalogPick ? { catalogPick: m.catalogPick } : {}),
        })),
        equipo: equipos.map((e) => ({
          origIndex: e.origIndex,
          descripcion: e.nombre.trim() || '(sin descripción)',
          horas_uso_por_unidad: e.horas || 0,
          precio_unitario: (e.costoHorario || 0) > 0 ? e.costoHorario : null,
          ...(e.origIndex == null && e.catalogPick ? { catalogPick: e.catalogPick } : {}),
        })),
        herramienta_pct: herramientaPct,
        ...(rendBase > 0 ? { rendimiento_base: rendBase } : {}),
      });
      setEditDirty(false);
    } finally {
      setSavingEdit(false);
    }
  }

  if (!matrix) {
    return (
      <div className={styles.analysisCard}>
        <div className={styles.analysisHeader}>
          <div>
            <div className={styles.analysisKicker}>Análisis de precio unitario</div>
            <div className={styles.analysisTitle}>
              {concepto.clave && <span style={{ color: tokens.colorBrandForeground1, marginRight: 8 }}>{concepto.clave}</span>}
              {concepto.descripcion}
            </div>
          </div>
          <Button onClick={onBack} appearance="subtle">← Volver al listado</Button>
        </div>
        <div style={{ padding: 22 }}>
          <MessageBar intent="error">
            <MessageBarBody>
              Este concepto no tiene matriz generada (falló en la fase de análisis con inteligencia artificial).
            </MessageBarBody>
          </MessageBar>
        </div>
      </div>
    );
  }

  // Reducción de rendimiento agregada (resultado, no parámetro) para el rollup.
  const redMo = rendBase > 0 && rendAdj > 0 && rendAdj < rendBase ? 1 - rendAdj / rendBase : 0;
  const srFromCatalog = concepto.apu_ia_sr_cuadrilla != null;
  const cuadrillaNombre = matrix.cuadrilla
    ? (matrix.cuadrilla.tipo_cuadrilla
        || getCuadrillaComps(matrix.cuadrilla).map((c) => `${c.cantidad} ${c.categoria.replace(/_/g, ' ')}`).join(' + ')
        || 'Cuadrilla')
    : 'Cuadrilla';
  const rendUnidad = matrix.rendimiento_base_unidad?.split('/')[1] ?? 'jor';

  const OrigenBadge = ({ cat, fuente, title }: { cat: boolean; fuente?: string; title?: string }) => {
    if (cat) {
      return <span className={`${styles.origenBadge} ${styles.origenCat}`} title={title ?? 'Precio del catálogo'}>Cat.</span>;
    }
    // El ORIGEN es binario: Catálogo o Sintético. Que el precio venga de mercado
    // o falte es un detalle que va al tooltip, nunca a la etiqueta de origen.
    const tSint = title ?? (fuente === 'mercado' ? 'Precio de referencia de mercado — revisar'
      : fuente === 'missing' ? 'No se encontró precio — se creará en OPUS al importar para capturarlo'
      : 'Estimado con inteligencia artificial');
    return <span className={`${styles.origenBadge} ${styles.origenIa}`} title={tSint}>Analizado con IA</span>;
  };

  return (
    <>
      {/* Banner: contexto desactualizado */}
      {concepto.apu_ia_context_outdated && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 22px',
          background: 'var(--warn-weak)',
          borderTop: `1px solid var(--warn)`,
          borderBottom: `1px solid var(--warn)`,
          borderLeft: `4px solid var(--warn)`,
          fontSize: tokens.fontSizeBase300,
        }}>
          <span style={{ fontSize: 22 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: 'var(--warn)' }}>Matriz desactualizada</strong>
            <div style={{ color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, marginTop: 2 }}>
              El contexto del proyecto cambió después de generarse esta matriz. Los factores o parámetros aplicados pueden estar obsoletos. Vuelve al listado y vuelve a crear los análisis de precios unitarios con IA para actualizar.
            </div>
          </div>
        </div>
      )}

      {/* Layout 2 columnas estilo mockup: Composición | Condiciones+rollup+precio */}
      <div className={styles.apuInner}>

        {/* IZQ — Composición a costo directo */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 9, flexWrap: 'wrap' }}>
            <h5 className={styles.apuBlockH5} style={{ margin: 0 }}>Composición a costo directo</h5>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {hasRazonamiento && (
                <button type="button" className={styles.razonBtn} onClick={() => setRazonamientoOpen(true)} title="Ver el razonamiento, la memoria de decisiones y las validaciones de OPUS ai">
                  <span style={{ fontSize: 14 }}>🧠</span>
                  <span>Cómo lo pensó OPUS ai</span>
                  <span className={styles.razonBtnSpark}>✨</span>
                </button>
              )}
              <Button size="small" appearance="subtle" icon={<Document16Regular />} onClick={() => setMemoryOpen(true)}>Ver memoria de cálculo</Button>
            </div>
          </div>

          {(() => {
            const matsSimples = materiales.map((m, i) => ({ m, i })).filter(({ m }) => !m.esAuxiliar);
            const auxiliares = materiales.map((m, i) => ({ m, i })).filter(({ m }) => m.esAuxiliar);
            const subMatSimples = matsSimples.reduce((s, { m }) => s + (m.precio || 0) * (m.cantidadNeta || 0) * (1 + (m.desperdicio || 0) / 100), 0);
            const subAux = auxiliares.reduce((s, { m }) => s + (m.precio || 0) * (m.cantidadNeta || 0) * (1 + (m.desperdicio || 0) / 100), 0);
            const numCol = 9;

            const matRow = (m: EditMaterial, i: number) => {
              const total = (m.cantidadNeta || 0) * (1 + (m.desperdicio || 0) / 100);
              const sub = (m.precio || 0) * total;
              return (
                <React.Fragment key={i}>
                  <tr>
                    {/* Identidad (descripción/unidad): editable SOLO si el renglón no viene
                        del catálogo — un recurso de catálogo ES ese recurso; para otro se
                        quita y se busca/agrega (QA 2026-07-03). Auxiliares tampoco. */}
                    <td className={`${styles.insTd} ${styles.insTdFirst}`} id={`mat-${i}`}>
                      {m.esAuxiliar || m.claveCatalogo
                        ? <span title={m.claveCatalogo ? 'Recurso del catálogo: la descripción no se edita. Para otro recurso, quítalo y busca/agrega el correcto.' : undefined}>{m.nombre}</span>
                        : <Input className={styles.insCellInput} size="small" style={{ width: '100%', minWidth: 260 }} value={m.nombre} onChange={(_, d) => updateMaterial(i, 'nombre', d.value)} placeholder="Descripción del insumo…" />}
                    </td>
                    <td className={styles.insTd}>
                      {m.esAuxiliar || m.claveCatalogo
                        ? (m.unidad || '—')
                        : <Input className={styles.insCellInput} size="small" style={{ width: 64 }} value={m.unidad} onChange={(_, d) => updateMaterial(i, 'unidad', d.value)} placeholder="Un." />}
                    </td>
                    <td className={styles.insTd}><Input className={styles.insCellInput} size="small" type="number" value={String(m.cantidadNeta)} onChange={(_, d) => updateMaterial(i, 'cantidadNeta', parseFloat(d.value) || 0)} /></td>
                    <td className={styles.insTd}><Input className={styles.insCellInput} size="small" type="number" value={String(m.desperdicio)} onChange={(_, d) => updateMaterial(i, 'desperdicio', parseFloat(d.value) || 0)} /></td>
                    <td className={styles.insTd} style={{ fontVariantNumeric: 'tabular-nums' }}>{total.toLocaleString('es-MX', { maximumFractionDigits: 6 })}</td>
                    <td className={styles.insTd}><Input className={styles.insCellInput} size="small" type="number" value={String(m.precio)} onChange={(_, d) => updateMaterial(i, 'precio', parseFloat(d.value) || 0)} /></td>
                    <td className={`${styles.insTd} ${styles.insImp}`}>${fmt(sub)}</td>
                    <td className={`${styles.insTd} ${styles.insTdFirst}`}><OrigenBadge cat={!!m.claveCatalogo} fuente={m.fuentePrecio} title={m.notaMercado} /></td>
                    <td className={styles.insTd}><span className={styles.rowAction} onClick={() => removeMaterial(i)}><Dismiss16Regular /></span></td>
                  </tr>
                  {m.esAuxiliar && (() => {
                    const subM = (matrix.materiales[i] as ApuIaInsumo | undefined)?.sub_receta;
                    const nMat = subM?.materiales?.length ?? 0;
                    const nMo = getCuadrillaComps(subM?.cuadrilla).length;
                    const nEq = (subM as { equipo?: unknown[] } | undefined)?.equipo?.length ?? 0;
                    const tot = nMat + nMo + nEq;
                    return (
                      <tr><td colSpan={numCol} className={styles.insNote}>
                        <a className={styles.auxLink} onClick={() => setExpandedAux((prev) => ({ ...prev, [i]: !prev[i] }))} style={{ fontWeight: tokens.fontWeightSemibold }}>
                          {expandedAux[i] ? '▼ Ocultar' : '▶ Ver'} composición del auxiliar
                          {tot > 0 && <span style={{ marginLeft: 6, color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightRegular }}>({nMat} mat · {nMo} MO{nEq > 0 ? ` · ${nEq} eq` : ''})</span>}
                        </a>
                        {expandedAux[i] && (
                          <AuxSubRecetaView
                            material={matrix.materiales[i] as ApuIaInsumo}
                            conceptId={concepto.id}
                            matIdx={i}
                            onResolved={(subReceta) => {
                              if (matrix.materiales[i]) {
                                (matrix.materiales[i] as ApuIaInsumo).sub_receta = subReceta as unknown as ApuIaInsumo['sub_receta'];
                                setExpandedAux((prev) => ({ ...prev, [i]: true }));
                              }
                            }}
                          />
                        )}
                      </td></tr>
                    );
                  })()}
                  {!m.esAuxiliar && m.claveCatalogo && (
                    <tr><td colSpan={numCol} className={styles.insNote} style={{ color: 'var(--ok)' }}>
                      ✓ Recurso del catálogo · <code style={{ fontSize: tokens.fontSizeBase100, background: tokens.colorNeutralBackground2, padding: '1px 4px', borderRadius: 3 }}>{m.claveCatalogo}</code>{m.descCatalogo && <> · <span style={{ color: tokens.colorNeutralForeground2 }}>{m.descCatalogo}</span></>}
                    </td></tr>
                  )}
                  {!m.esAuxiliar && m.incluyeMo && (
                    <tr><td colSpan={numCol} className={styles.insNote} style={{ color: 'var(--warn)' }} title="El catálogo registra este insumo como concepto terminado: su precio ya incluye mano de obra.">
                      ⚠ Este precio ya incluye mano de obra (concepto del catálogo) — revisa el posible doble conteo con la cuadrilla.
                    </td></tr>
                  )}
                  {!m.esAuxiliar && !m.claveCatalogo && m.fuentePrecio === 'missing' && (
                    <tr><td colSpan={numCol} className={styles.insNote} style={{ color: 'var(--warn)' }}>
                      ⚠ No se encontró precio — el insumo se creará en OPUS al importar para que lo captures
                    </td></tr>
                  )}
                </React.Fragment>
              );
            };

            const matHead = (firstLabel: string) => (
              <thead><tr>
                <th className={`${styles.insTh} ${styles.insThFirst}`}>{firstLabel}</th>
                <th className={styles.insTh}>Unidad</th>
                <th className={styles.insTh}>Cantidad</th>
                <th className={styles.insTh}>% Desp.</th>
                <th className={styles.insTh}>Total</th>
                <th className={styles.insTh}>Costo</th>
                <th className={styles.insTh}>Importe</th>
                <th className={`${styles.insTh} ${styles.insThFirst}`}>Origen</th>
                <th className={styles.insTh} />
              </tr></thead>
            );

            return (
              <>
                <div className={styles.secLab}>Materiales</div>
                <table className={styles.ins}>
                  {matHead('Material')}
                  <tbody>
                    {matsSimples.length === 0
                      ? <tr><td colSpan={numCol} className={styles.insTd} style={{ textAlign: 'center', fontStyle: 'italic' }}>Sin materiales</td></tr>
                      : matsSimples.map(({ m, i }) => matRow(m, i))}
                    <tr>
                      <td colSpan={6} className={`${styles.insTd} ${styles.insSub} ${styles.insTdFirst}`}>Subtotal materiales</td>
                      <td className={`${styles.insTd} ${styles.insSub} ${styles.insImp}`}>${fmt(subMatSimples)}</td>
                      <td colSpan={2} className={`${styles.insTd} ${styles.insSub}`} />
                    </tr>
                  </tbody>
                </table>
                {MOSTRAR_EDICION_INSUMOS && (<>
                  <a className={styles.footLink} onClick={addMaterial} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <Add16Regular /> Añadir insumo
                  </a>
                  <a className={styles.footLink} onClick={() => setPickerFor('material')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, marginLeft: 14 }}>
                    🔍 Buscar en catálogos
                  </a>
                </>)}

                {auxiliares.length > 0 && (
                  <>
                    <div className={styles.secLab}>Auxiliares · matrices compuestas</div>
                    <table className={styles.ins}>
                      {matHead('Auxiliar')}
                      <tbody>
                        {auxiliares.map(({ m, i }) => matRow(m, i))}
                        <tr>
                          <td colSpan={6} className={`${styles.insTd} ${styles.insSub} ${styles.insTdFirst}`}>Subtotal auxiliares</td>
                          <td className={`${styles.insTd} ${styles.insSub} ${styles.insImp}`}>${fmt(subAux)}</td>
                          <td colSpan={2} className={`${styles.insTd} ${styles.insSub}`} />
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}
              </>
            );
          })()}

          {/* MANO DE OBRA */}
          <div className={styles.secLab}>Mano de obra</div>
          <table className={styles.ins}>
            <thead><tr>
              <th className={`${styles.insTh} ${styles.insThFirst}`}>Cuadrilla</th>
              <th className={styles.insTh}>Un.</th>
              <th className={styles.insTh}>Rend. base</th>
              <th className={styles.insTh}>Rend. ajust.</th>
              <th className={styles.insTh}>Costo / {rendUnidad}</th>
              <th className={styles.insTh}>Importe</th>
              <th className={`${styles.insTh} ${styles.insThFirst}`}>Origen</th>
            </tr></thead>
            <tbody>
              <tr>
                <td className={`${styles.insTd} ${styles.insTdFirst}`} title={matrix.cuadrilla?.tipo_cuadrilla ?? undefined}>{cuadrillaNombre}</td>
                <td className={styles.insTd}>{rendUnidad}</td>
                <td className={styles.insTd}>{Number(rendBase).toFixed(2)}</td>
                <td className={styles.insTd}>{Number(rendAdj).toFixed(2)}</td>
                <td className={styles.insTd}>${fmt(srEfectivo)}</td>
                <td className={`${styles.insTd} ${styles.insImp}`}>${fmt(subMo)}</td>
                <td className={`${styles.insTd} ${styles.insTdFirst}`}><OrigenBadge cat={srFromCatalog} /></td>
              </tr>
              <tr>
                <td colSpan={5} className={`${styles.insTd} ${styles.insSub} ${styles.insTdFirst}`}>
                  Subtotal mano de obra{redMo > 0 ? <span className={styles.condEff}> · rend. −{(redMo * 100).toFixed(1)}%</span> : null}
                </td>
                <td className={`${styles.insTd} ${styles.insSub} ${styles.insImp}`}>${fmt(subMo)}</td>
                <td className={`${styles.insTd} ${styles.insSub}`} />
              </tr>
            </tbody>
          </table>

          {/* EQUIPO */}
          <div className={styles.secLab}>Equipo</div>
          <table className={styles.ins}>
            <thead><tr>
              <th className={`${styles.insTh} ${styles.insThFirst}`}>Equipo</th>
              <th className={styles.insTh}>Costo / hr</th>
              <th className={styles.insTh}>Hrs / jor</th>
              <th className={styles.insTh}>Importe</th>
              <th className={`${styles.insTh} ${styles.insThFirst}`}>Origen</th>
              <th className={styles.insTh} />
            </tr></thead>
            <tbody>
              {equipos.length === 0 ? (
                <tr><td colSpan={6} className={styles.insTd} style={{ textAlign: 'center', fontStyle: 'italic' }}>Sin equipo · este concepto puede no requerir equipo</td></tr>
              ) : (
                equipos.map((e, i) => {
                  // `e.horas` ya está por unidad del concepto → no se divide.
                  const sub = e.costoHorario * e.horas;
                  return (
                    <React.Fragment key={i}>
                      <tr>
                        <td className={`${styles.insTd} ${styles.insTdFirst}`}>
                          {/* Identidad editable solo si NO viene del catálogo (QA 2026-07-03). */}
                          {e.claveCatalogo
                            ? <span title="Recurso del catálogo: la descripción no se edita. Para otro recurso, quítalo y busca/agrega el correcto.">{e.nombre}</span>
                            : <Input className={styles.insCellInput} size="small" style={{ width: '100%', minWidth: 200 }} value={e.nombre} onChange={(_, d) => updateEquipo(i, 'nombre', d.value)} placeholder="Descripción del equipo…" />}
                        </td>
                        <td className={styles.insTd}>
                          <a className={styles.priceLink} onClick={() => setCostoHorarioDialog({ idx: i, nombre: e.nombre, precio: e.costoHorario })} title="Ver desglose del costo horario (RLOPSRM Art. 195)">
                            ${fmt(e.costoHorario)} <Info16Regular />
                          </a>
                        </td>
                        <td className={styles.insTd}><Input className={styles.insCellInput} size="small" type="number" value={String(e.horas)} onChange={(_, d) => updateEquipo(i, 'horas', parseFloat(d.value) || 0)} /></td>
                        <td className={`${styles.insTd} ${styles.insImp}`}>${fmt(sub)}</td>
                        <td className={`${styles.insTd} ${styles.insTdFirst}`}><OrigenBadge cat={!!e.claveCatalogo} fuente={e.fuentePrecio} title={e.notaMercado} /></td>
                        <td className={styles.insTd}><span className={styles.rowAction} onClick={() => removeEquipo(i)}><Dismiss16Regular /></span></td>
                      </tr>
                      {!e._isNew && e.claveCatalogo && (
                        <tr><td colSpan={6} className={styles.insNote} style={{ color: 'var(--ok)' }}>
                          ✓ Recurso del catálogo · <code style={{ fontSize: tokens.fontSizeBase100, background: tokens.colorNeutralBackground2, padding: '1px 4px', borderRadius: 3 }}>{e.claveCatalogo}</code>
                        </td></tr>
                      )}
                      {!e._isNew && !e.claveCatalogo && e.fuentePrecio === 'missing' && (
                        <tr><td colSpan={6} className={styles.insNote} style={{ color: 'var(--warn)' }}>
                          ⚠ No se encontró precio — el insumo se creará en OPUS al importar para que lo captures
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
              <tr>
                <td colSpan={3} className={`${styles.insTd} ${styles.insSub} ${styles.insTdFirst}`}>
                  Subtotal equipo{redMo > 0 ? <span className={styles.condEff}> · rend. −{(redMo * 100).toFixed(1)}%</span> : null}
                </td>
                <td className={`${styles.insTd} ${styles.insSub} ${styles.insImp}`}>${fmt(subEq)}</td>
                <td colSpan={2} className={`${styles.insTd} ${styles.insSub}`} />
              </tr>
            </tbody>
          </table>

          {/* HERRAMIENTA MENOR — categoría DISTINTA del equipo. Es el % de
              herramienta menor sobre la mano de obra (convención APU MX). Viaja a
              OPUS como su propio concepto (herramientaPct/subHerramienta), NO como
              equipo. */}
          <div className={styles.secLab}>Herramienta menor</div>
          <table className={styles.ins}>
            <tbody>
              <tr>
                <td colSpan={3} className={`${styles.insTd} ${styles.insSub} ${styles.insTdFirst}`}>Herramienta menor · {herramientaPct}% s/MO</td>
                <td className={`${styles.insTd} ${styles.insSub} ${styles.insImp}`}>${fmt(subHer)}</td>
                <td colSpan={2} className={`${styles.insTd} ${styles.insSub}`} />
              </tr>
            </tbody>
          </table>
          {MOSTRAR_EDICION_INSUMOS && (<>
            <a className={styles.footLink} onClick={addEquipo} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
              <Add16Regular /> Añadir equipo
            </a>
            <a className={styles.footLink} onClick={() => setPickerFor('equipo')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, marginLeft: 14 }}>
              🔍 Buscar en catálogos
            </a>
          </>)}

          {pickerFor && pickerQuoteId && (
            <CatalogPickerDialog
              quoteId={pickerQuoteId}
              kind={pickerFor}
              onPick={onCatalogPicked}
              onClose={() => setPickerFor(null)}
            />
          )}

        </div>

        {/* DER — Precio (arriba) · acciones · condiciones colapsadas abajo */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 9 }}>
            <h5 className={styles.apuBlockH5} style={{ margin: 0 }}>Resumen de costo directo</h5>
            {concepto.apu_ia_review_status !== 'approved' && concepto.apu_ia_review_status !== 'rejected' && (
              <Button size="small" appearance="subtle" title="Vuelve a crear este concepto con OPUS ai (cuenta como 1 análisis de precio unitario generado)"
                onClick={async () => {
                  const avisoEdicion = (matrix.editado_manual || editDirty)
                    ? 'ESTE ANÁLISIS TIENE EDICIONES MANUALES — volver a crearlo con inteligencia artificial las reemplaza.\n\n'
                    : '';
                  if (!(await confirm({
                    title: 'Volver a crear el análisis',
                    message: `${avisoEdicion}¿Volver a crear el análisis con inteligencia artificial de este concepto?\n\nSe perderá el análisis actual y se generará uno nuevo. Cuenta como 1 análisis de precio unitario generado en tu cuota.`,
                    confirmLabel: 'Regenerar',
                  }))) return;
                  await onRegenerate();
                }}>
                ↻ Regenerar
              </Button>
            )}
          </div>

          {/* Roll-up */}
          <div className={styles.apuRollup}>
            <div className={styles.apuLine}><span className={styles.apuLineD}>Materiales</span><span className={styles.apuLineA}>${fmt(subMat)}</span></div>
            <div className={styles.apuLine}>
              <span className={styles.apuLineD}>Mano de obra{redMo > 0 ? <span className={styles.condEff} style={{ marginLeft: 4 }}>(rend. −{(redMo * 100).toFixed(1)}%)</span> : null}</span>
              <span className={styles.apuLineA}>${fmt(subMo)}</span>
            </div>
            <div className={styles.apuLine}><span className={styles.apuLineD}>Equipo</span><span className={styles.apuLineA}>${fmt(subEq)}</span></div>
            <div className={`${styles.apuLine} ${styles.apuLineLast}`}><span className={styles.apuLineD}>Herramienta menor</span><span className={styles.apuLineA}>${fmt(subHer)}</span></div>
          </div>
          <div className={styles.apuTotal}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600 }}>Precio unitario · costo directo</span>
            <span className={styles.apuPu}>${fmt(cdTotal)}</span>
          </div>

          {/* Acciones */}
          <div className={styles.apuActions}>
            {matrix.editado_manual && !editDirty && (
              <Badge size="medium" appearance="tint" color="brand" title={`Composición editada manualmente el ${new Date(matrix.editado_manual.at).toLocaleString('es-MX')}`}>
                ✎ Editado manualmente · {new Date(matrix.editado_manual.at).toLocaleDateString('es-MX')}
              </Badge>
            )}
            {editDirty && (
              <Button
                size="small"
                appearance="primary"
                disabled={savingEdit}
                title="Guarda la composición editada; el estado de revisión no cambia"
                onClick={() => void onGuardarEdicion()}
              >
                {savingEdit ? 'Guardando…' : '💾 Guardar cambios'}
              </Button>
            )}
            {concepto.apu_ia_review_status === 'approved' ? (
              <>
                <Badge size="large" appearance="filled" color="success">✓ Aprobada</Badge>
                <Button size="small" appearance="subtle" onClick={async () => { await onReviewAction('reopen'); }}>Reabrir</Button>
              </>
            ) : concepto.apu_ia_review_status === 'rejected' ? (
              <>
                <Badge size="large" appearance="filled" color="danger">✕ Rechazada</Badge>
                <Button size="small" appearance="subtle" onClick={async () => { await onReviewAction('reopen'); }}>Reabrir</Button>
              </>
            ) : null}
          </div>

          {/* Ajustar condiciones de obra — OCULTO (2026-07-25, AI-19): al abrir un
              APU el foco es el precio. La lógica de recálculo (recompute-apu) sigue
              intacta; reactivar la UI = MOSTRAR_AJUSTE_CONDICIONES = true. */}
          {MOSTRAR_AJUSTE_CONDICIONES && (
          <div className={styles.ajusteWrap}>
            <button
              type="button"
              className={styles.ajusteToggle}
              aria-expanded={showAjuste}
              onClick={() => setShowAjuste((v) => !v)}
            >
              <span>{showAjuste ? '▾' : '▸'} Ajustar condiciones de obra</span>
              {factores.length > 0 && <span className={styles.ajusteCount}>{factores.length}</span>}
            </button>
            {showAjuste && (
              <div style={{ marginTop: 10 }}>
                <p className={styles.condIntro}>
                  Prende o apaga las condiciones que aplican a este concepto y pulsa Recalcular; el precio se ajusta.
                  Las condiciones bajan el rendimiento de mano de obra/equipo.
                </p>
                {factores.length === 0 ? (
                  <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, padding: '6px 0' }}>
                    Ningún factor de obra disparó en este concepto.
                  </div>
                ) : factores.map((f) => {
                  const on = !offIds.has(f.id);
                  const target = f.pega_en ?? [];
                  const targetLabel = target.includes('mano_obra') && target.includes('equipo') ? 'mano de obra y equipo'
                    : target.includes('mano_obra') ? 'mano de obra'
                    : target.includes('equipo') ? 'equipo' : '';
                  const adj = Number(f.ajuste_pct) || 0;
                  const scope = targetLabel ? 'Rendimiento' : 'Proyecto';
                  return (
                    <div key={f.id} className={`${styles.condRow}${on ? '' : ` ${styles.condOff}`}`}>
                      <span className={styles.condMeta}>
                        <span className={styles.scopeChip}>{scope}</span>
                        <span style={{ color: 'var(--text-primary)' }}>{f.justificacion || f.id}</span>
                        {targetLabel && adj !== 0
                          ? <span className={styles.condEff} style={{ color: adj < 0 ? 'var(--warn)' : 'var(--ok)' }}>{adj < 0 ? 'menor' : 'mayor'} rendimiento · {targetLabel}</span>
                          : <span className={styles.condEff}>sin efecto en rendimiento</span>}
                        {f.evidencia && <span className={styles.condEff}>· {f.evidencia}</span>}
                        {(f.confianza_inferencia === 'media' || f.confianza_inferencia === 'baja') && (
                          <span className={styles.condEff} style={{ color: 'var(--warn)' }}>· confianza {f.confianza_inferencia}, confírmalo</span>
                        )}
                      </span>
                      <span
                        className={`${styles.sw}${on ? ` ${styles.swOn}` : ''}`}
                        role="switch"
                        aria-checked={on}
                        tabIndex={0}
                        onClick={() => setOffIds((prev) => { const n = new Set(prev); if (n.has(f.id)) n.delete(f.id); else n.add(f.id); return n; })}
                      >
                        <span className={`${styles.swKnob}${on ? ` ${styles.swKnobOn}` : ''}`} />
                      </span>
                    </div>
                  );
                })}
                {factores.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <Button size="small" appearance="outline" disabled={recomputing}
                      onClick={async () => {
                        setRecomputing(true);
                        await onRecompute(factores.map((f) => ({ id: f.id, activo: !offIds.has(f.id) })));
                        setRecomputing(false);
                      }}>
                      {recomputing ? 'Recalculando…' : '↻ Recalcular'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Razonamiento IA + memoria de decisiones + validaciones → ahora dentro del
          modal "Cómo lo pensó OPUS ai" (botón), ya NO inline. */}
      <RazonamientoDialog
        open={razonamientoOpen}
        onClose={() => setRazonamientoOpen(false)}
        concepto={concepto}
        styles={styles}
      />

      {/* Modal: Ver memoria de cálculo */}
      <MemoryDialog
        open={memoryOpen}
        onClose={() => setMemoryOpen(false)}
        concepto={concepto}
        srEfectivo={srEfectivo}
        rendAdj={rendAdj}
        subtotales={{ subMat, subMo, subEq, subHer, cdTotal, importe }}
        isStaff={isStaff}
      />

      {/* Modal: desglose costo horario RLOPSRM */}
      <CostoHorarioDialog
        data={costoHorarioDialog}
        onClose={() => setCostoHorarioDialog(null)}
      />
    </>
  );
}

// =============================================================
// Modal "Cómo lo pensó OPUS ai" — razonamiento + memoria de decisiones +
// validaciones/alertas (antes iban inline debajo del APU).
// =============================================================

function RazonamientoDialog({
  open, onClose, concepto, styles,
}: {
  open: boolean;
  onClose: () => void;
  concepto: ApuIaConcepto;
  styles: ReturnType<typeof useStyles>;
}) {
  const matrix = concepto.apu_ia_matrix;
  const razon: Array<{ k: string; v: string }> = [];
  if (matrix?.razonamiento_alcance) razon.push({ k: 'Alcance', v: matrix.razonamiento_alcance });
  if (matrix?.razonamiento_cuadrilla) razon.push({ k: 'Cuadrilla', v: matrix.razonamiento_cuadrilla });
  if (matrix?.razonamiento_equipo) razon.push({ k: 'Equipo', v: matrix.razonamiento_equipo });
  if (matrix?.razonamiento_desperdicios) razon.push({ k: 'Desperdicios', v: matrix.razonamiento_desperdicios });
  const tieneValidaciones =
    (concepto.apu_ia_validaciones?.length ?? 0) > 0 ||
    (concepto.apu_ia_alertas?.length ?? 0) > 0 ||
    (concepto.apu_ia_preguntas_pendientes?.length ?? 0) > 0;
  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 820, width: '90vw' }}>
        <DialogBody>
          <DialogTitle>🧠 Cómo lo pensó OPUS ai</DialogTitle>
          <DialogContent style={{ fontSize: 13, lineHeight: 1.6 }}>
            {razon.length > 0 && (
              <Section title="Razonamiento">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {razon.map((r) => (
                    <div key={r.k}>
                      <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{r.k}</div>
                      <div>{r.v}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            {matrix?.memoria_decisiones && (
              <Section title="Memoria de decisiones">
                <div style={{ whiteSpace: 'pre-wrap' }}>{matrix.memoria_decisiones}</div>
              </Section>
            )}
            {/* Caso real de que no haya explicación guardada. Se dice tal cual:
                antes acá vivía una frase que prometía el detalle "bajo petición",
                y no había dónde pedirlo. */}
            {razon.length === 0 && !matrix?.memoria_decisiones && (
              <Section title="Razonamiento">
                <div>
                  De este análisis no quedó guardada la explicación. El precio y su desglose sí están
                  completos; para tener la explicación, vuelve a crear el análisis con inteligencia artificial.
                </div>
              </Section>
            )}
            {tieneValidaciones && (
              <Section title="Validaciones y alertas">
                <ValidacionesBlock
                  validaciones={concepto.apu_ia_validaciones ?? []}
                  alertas={concepto.apu_ia_alertas ?? []}
                  preguntas={concepto.apu_ia_preguntas_pendientes ?? []}
                  styles={styles}
                />
              </Section>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>Cerrar</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// =============================================================
// Modal "Ver memoria de cálculo"
// =============================================================

function MemoryDialog({
  open, onClose, concepto, srEfectivo, rendAdj, subtotales, isStaff,
}: {
  open: boolean;
  onClose: () => void;
  concepto: ApuIaConcepto;
  srEfectivo: number;
  rendAdj: number;
  subtotales: { subMat: number; subMo: number; subEq: number; subHer: number; cdTotal: number; importe: number };
  isStaff: boolean;
}) {
  const matrix = concepto.apu_ia_matrix;
  const factores = concepto.apu_ia_factores_aplicados ?? [];
  const factoresAplican = factores.filter((f) => f.dispara);

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 920, width: '90vw' }}>
        <DialogBody>
          <DialogTitle>Memoria de cálculo — auditoría del PU</DialogTitle>
          <DialogContent style={{ fontSize: 13, lineHeight: 1.6 }}>

            {/* Concepto */}
            <Section title="1. Concepto">
              {concepto.clave && <KV k="Clave" v={concepto.clave} />}
              <KV k="Descripción" v={concepto.descripcion} />
              <KV k="Unidad" v={concepto.unidad} />
              <KV k="Cantidad" v={String(concepto.cantidad)} />
            </Section>

            {/* Rendimiento + factores */}
            <Section title="2. Rendimiento y factores aplicados">
              <KV k="Rendimiento base" v={`${concepto.apu_ia_rendimiento_base ?? '—'} ${matrix?.rendimiento_base_unidad ?? ''}`} />
              <KV k="Rendimiento ajustado" v={`${rendAdj} ${matrix?.rendimiento_base_unidad ?? ''}`} />
              {factoresAplican.length === 0 ? (
                <div style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>Ningún factor disparó.</div>
              ) : (
                <>
                  <div style={{ marginTop: 8, fontWeight: 600 }}>Factores aplicados:</div>
                  {factoresAplican.map((f) => (
                    <div key={f.id} style={{ paddingLeft: 16, marginBottom: 8 }}>
                      {isStaff ? (
                        // Solo staff Ecosoft ve el parámetro de calibración (% + fuente + id).
                        <>
                          <code style={{ background: tokens.colorNeutralBackground2, padding: '1px 6px', borderRadius: 3 }}>{f.id}</code>
                          {' '}
                          <strong style={{ color: f.ajuste_pct < 0 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}>
                            {f.ajuste_pct > 0 ? '+' : ''}{f.ajuste_pct}%
                          </strong>
                          {f.fuente && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: tokens.colorNeutralForeground4, fontStyle: 'italic' }}>
                              📖 {f.fuente}
                            </span>
                          )}
                        </>
                      ) : (
                        // Cliente: solo el efecto cualitativo, nunca el porcentaje.
                        <strong style={{ color: f.ajuste_pct < 0 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}>
                          {f.ajuste_pct < 0 ? 'Menor rendimiento' : f.ajuste_pct > 0 ? 'Mayor rendimiento' : 'Sin efecto en rendimiento'}
                        </strong>
                      )}
                      <div style={{ paddingLeft: 14, color: tokens.colorNeutralForeground3, fontSize: 12 }}>{f.justificacion}</div>
                    </div>
                  ))}
                </>
              )}
            </Section>

            {/* Cuadrilla */}
            <Section title="3. Mano de obra">
              <KV
                k="Composición"
                v={getCuadrillaComps(concepto.apu_ia_matrix?.cuadrilla).map((c) => `${c.cantidad} ${c.categoria.replace(/_/g, ' ')}`).join(' + ') || '—'}
              />
              <KV k="Salario real / jornada" v={`$${fmt(srEfectivo)}`} />
              {concepto.apu_ia_sr_cuadrilla != null && (
                <div style={{ fontSize: 11, color: tokens.colorPaletteGreenForeground1, marginLeft: 16 }}>
                  ✓ Salario tomado del catálogo del cliente
                </div>
              )}
              {getCuadrillaComps(matrix?.cuadrilla).length > 0 && (
                <ul style={{ paddingLeft: 32, marginTop: 6, fontSize: 12 }}>
                  {getCuadrillaComps(matrix?.cuadrilla).map((c, i) => (
                    <li key={i}>
                      {c.cantidad} × {c.categoria.replace(/_/g, ' ')}
                      {(c as unknown as { precio_unitario?: number }).precio_unitario != null && (
                        <> · ${(c as unknown as { precio_unitario: number }).precio_unitario.toFixed(2)}/jor</>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Subtotales */}
            <Section title="4. Subtotales (por unidad)">
              <KV k="Materiales" v={`$${fmt(subtotales.subMat)}`} />
              <KV k="Mano de obra" v={`$${fmt(subtotales.subMo)}`} />
              <KV k="Equipo" v={`$${fmt(subtotales.subEq)}`} />
              <KV k="Herramienta menor" v={`$${fmt(subtotales.subHer)}`} />
              <KV k="Costo directo" v={`$${fmt(subtotales.cdTotal)} / ${concepto.unidad}`} bold />
              <KV k="Importe total" v={`$${fmt(subtotales.importe)}`} bold />
            </Section>

            {/* Validaciones */}
            <Section title="5. Validaciones">
              {(concepto.apu_ia_validaciones ?? []).map((v) => (
                <div key={v.id} style={{ marginBottom: 4 }}>
                  <span style={{
                    color: v.tipo === 'success' ? tokens.colorPaletteGreenForeground1
                      : v.tipo === 'warning' ? tokens.colorPaletteDarkOrangeForeground1
                      : tokens.colorPaletteRedForeground1,
                  }}>
                    {v.tipo === 'success' ? '✓' : v.tipo === 'warning' ? '⚠' : '✕'} {v.msg}
                  </span>
                </div>
              ))}
            </Section>

            {/* Memoria del modelo */}
            {matrix?.memoria_decisiones && (
              <Section title="6. Memoria de decisiones del modelo">
                <div style={{ fontStyle: 'italic', color: tokens.colorNeutralForeground2, padding: 12, background: tokens.colorNeutralBackground2, borderRadius: 6 }}>
                  {matrix.memoria_decisiones}
                </div>
              </Section>
            )}

            {/* Preguntas pendientes */}
            {(concepto.apu_ia_preguntas_pendientes?.length ?? 0) > 0 && (
              <Section title={`${matrix?.memoria_decisiones ? 7 : 6}. Preguntas pendientes`}>
                {concepto.apu_ia_preguntas_pendientes!.map((q, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>· {q}</div>
                ))}
              </Section>
            )}

            {/* Auditoría — número dinámico (las secciones 6 y 7 son condicionales) */}
            <Section title={`${6 + (matrix?.memoria_decisiones ? 1 : 0) + ((concepto.apu_ia_preguntas_pendientes?.length ?? 0) > 0 ? 1 : 0)}. Auditoría`}>
              <KV
                k="Origen del precio"
                v={
                  concepto.apu_ia_cotejo_origen === 'alta' ? 'Coincidencia alta con el catálogo'
                    : concepto.apu_ia_cotejo_origen === 'media' ? 'Coincidencia media con el catálogo'
                    : concepto.apu_ia_cotejo_origen === 'sin_candidato' ? 'Sin coincidencia — generado por inteligencia artificial'
                    : '—'
                }
              />
              <KV
                k="Estado de revisión"
                v={
                  concepto.apu_ia_review_status === 'approved' ? 'Aprobada'
                    : concepto.apu_ia_review_status === 'rejected' ? 'Rechazada'
                    : 'Pendiente'
                }
              />
              {concepto.apu_ia_reviewed_at && <KV k="Revisado en" v={new Date(concepto.apu_ia_reviewed_at).toLocaleString('es-MX')} />}
            </Section>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>Cerrar</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h3 style={{
        fontSize: 13,
        fontWeight: 600,
        color: tokens.colorBrandForeground1,
        marginBottom: 8,
        paddingBottom: 4,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      }}>{title}</h3>
      {children}
    </div>
  );
}

function KV({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '180px 1fr',
      gap: 12,
      padding: '3px 0',
      fontWeight: bold ? 600 : 400,
    }}>
      <span style={{ color: tokens.colorNeutralForeground3 }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

// =============================================================
// Modal: desglose del costo horario (RLOPSRM Art. 195)
// =============================================================

function CostoHorarioDialog({
  data, onClose,
}: {
  data: { idx: number; nombre: string; precio: number } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={data !== null} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 680 }}>
        <DialogBody>
          <DialogTitle>
            Desglose del costo horario · RLOPSRM Art. 195
          </DialogTitle>
          <DialogContent style={{ fontSize: 13, lineHeight: 1.6 }}>
            {data && (
              <>
                <div style={{ marginBottom: 14, padding: '10px 14px', background: tokens.colorNeutralBackground2, borderRadius: 6 }}>
                  <strong>Equipo:</strong> {data.nombre || '(sin nombre)'}<br />
                  <strong>Costo horario actual:</strong> ${fmt(data.precio)} / hr
                </div>
                <p style={{ marginBottom: 12, color: tokens.colorNeutralForeground2 }}>
                  Según el <strong>Reglamento de la Ley de Obras Públicas (RLOPSRM) Art. 195</strong>,
                  el costo horario en obra pública se compone de 9 elementos. OPUS los calcula con los valores
                  vigentes del proyecto al importar; aquí ves la fórmula canónica:
                </p>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', width: 60 }}>Símbolo</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Componente</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Vm', 'Valor de adquisición del equipo', 'Precio nuevo vigente. Obra pública usa SIEMPRE equipo nuevo (no depreciado).'],
                      ['Vr', 'Valor de rescate', 'Estimación al final de vida útil (típicamente 10% de Vm).'],
                      ['Ve', 'Vida útil económica', 'Horas totales de operación efectiva esperadas.'],
                      ['Hea', 'Horas efectivas anuales', 'Horas reales de operación al año (típico 1500–2000 hrs).'],
                      ['i', 'Tasa de interés', 'CETES + sobretasa, tomado de los defaults del proyecto.'],
                      ['s', 'Prima de seguros', '% anual sobre Vm para cobertura.'],
                      ['Ko', 'Coeficiente operación', 'Mantenimiento + reparaciones + lubricantes (% sobre depreciación).'],
                      ['Combustible', 'Consumo diésel/gasolina', 'Litros/hora × precio vigente del combustible.'],
                      ['Operador', 'Salario del operador', 'Salario real (FSR) × horas reales de operación.'],
                    ].map(([sim, comp, desc]) => (
                      <tr key={sim} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: tokens.colorBrandForeground1 }}>{sim}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 600 }}>{comp}</td>
                        <td style={{ padding: '6px 8px', color: tokens.colorNeutralForeground3 }}>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{
                  marginTop: 14,
                  padding: '10px 14px',
                  background: 'rgba(74,158,255,0.08)',
                  border: '1px solid rgba(74,158,255,0.3)',
                  borderRadius: 6,
                  fontSize: 12,
                  color: tokens.colorNeutralForeground2,
                }}>
                  💡 OPUS aplica esta fórmula con los valores del catálogo del cliente
                  al importar el análisis de precio unitario con IA. El precio $${fmt(data.precio)}/hr que ves
                  aquí proviene del matching contra el catálogo cargado al proyecto.
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>Entendido</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// =============================================================
// Vista de composición al expandir matriz auxiliar
// =============================================================

function AuxSubRecetaView({
  material, conceptId, matIdx, onResolved,
}: {
  material: ApuIaInsumo;
  conceptId: string;
  matIdx: number;
  onResolved: (subReceta: Record<string, unknown>) => void;
}) {
  const fmtObra = useMonedaObra();
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sub = material.sub_receta;
  if (!sub) {
    return (
      <div style={{
        marginTop: 6,
        padding: 12,
        background: 'var(--warn-weak)',
        border: `1px dashed var(--warn)`,
        borderRadius: 6,
        fontSize: 12,
        color: tokens.colorPaletteDarkOrangeForeground1,
      }}>
        <strong>⚠ Composición no disponible</strong>
        <div style={{ marginTop: 4, color: tokens.colorNeutralForeground2 }}>
          La inteligencia artificial devolvió este insumo como matriz auxiliar (ref: <code>{(material as { ref_auxiliar_id?: string }).ref_auxiliar_id ?? '—'}</code>) pero no incluyó la composición. OPUS lo creará como recurso nuevo costo 0 para que el ingeniero defina la matriz.
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            size="small"
            appearance="primary"
            disabled={resolving}
            onClick={async () => {
              setResolving(true);
              setError(null);
              try {
                const r = await resolveAuxiliarReceta(conceptId, {
                  matIdx,
                  descripcion: material.descripcion_normalizada,
                  unidad: material.unidad,
                });
                onResolved(r.sub_receta);
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Error al pedir composición a la inteligencia artificial');
              } finally {
                setResolving(false);
              }
            }}
          >
            {resolving ? '⏳ Consultando…' : '🪄 Pedir a la inteligencia artificial que arme la composición'}
          </Button>
        </div>
        {error && (
          <div style={{ marginTop: 8, color: tokens.colorPaletteRedForeground1 }}>
            {error}
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, padding: '12px 12px 12px 15px', background: tokens.colorNeutralBackground2, borderRadius: 6, borderLeft: '3px solid var(--privado)', fontSize: 12 }}>
      {/* Header: rendimiento + cuadrilla */}
      <div style={{ marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
        <div style={{ color: tokens.colorNeutralForeground3, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Composición del auxiliar
        </div>
        <div style={{ marginTop: 4 }}>
          <strong>Rendimiento base:</strong> {sub.rendimiento_base} {sub.rendimiento_base_unidad}
        </div>
      </div>

      {/* Composición del auxiliar — UNA sola tabla: header único, columna Unidad
          propia, y separación NOTORIA por tipo (Mano de obra / Materiales / Equipo)
          vía filas-encabezado de grupo. % Desp. solo aplica a materiales. */}
      {(() => {
        // Importes en LA MONEDA DE LA OBRA (contexto `MonedaObraCtx`), no un '$' fijo.
        const money = fmtObra.money;
        const tdR = { padding: '4px 8px', textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const, verticalAlign: 'top' as const };
        const tdMuted = { ...tdR, color: tokens.colorNeutralForeground3 };
        const tdName = { padding: '4px 8px 4px 0', verticalAlign: 'top' as const };
        const tdUnit = { padding: '4px 8px', textAlign: 'left' as const, color: tokens.colorNeutralForeground3, verticalAlign: 'top' as const, whiteSpace: 'nowrap' as const };
        const origenNota = (enriched: { fuente?: string; clave_catalogo?: string; descripcion_catalogo?: string }) => (
          <>
            {enriched.fuente === 'catalogo' && enriched.clave_catalogo && (
              <div style={{ fontSize: 10, color: tokens.colorPaletteGreenForeground1 }}>
                ✓ <code>{enriched.clave_catalogo}</code> {enriched.descripcion_catalogo && `· ${enriched.descripcion_catalogo}`}
              </div>
            )}
            {enriched.fuente === 'missing' && (
              <div style={{ fontSize: 10, color: tokens.colorPaletteDarkOrangeForeground1 }}>
                ⚠ No se encontró precio — se creará en OPUS al importar
              </div>
            )}
          </>
        );
        const precioCell = (p: number | null | undefined) => (
          p != null
            ? <strong>{money(Number(p))}</strong>
            : <span style={{ color: tokens.colorPaletteDarkOrangeForeground1 }}>—</span>
        );
        const importeCell = (imp: number, has: boolean) => (
          has
            ? <strong>{money(imp)}</strong>
            : <span style={{ color: tokens.colorPaletteDarkOrangeForeground1 }}>—</span>
        );
        // Fila-encabezado de grupo: separación notoria entre tipos de recurso.
        const groupRow = (label: string, k: string) => (
          <tr key={k}>
            <td colSpan={6} style={{
              padding: '11px 8px 5px', fontSize: 10.5, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              color: tokens.colorNeutralForeground2,
              borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
            }}>{label}</td>
          </tr>
        );
        const rowBorder = (i: number) => ({ borderTop: i > 0 ? `1px solid ${tokens.colorNeutralStroke3}` : 'none' });
        const comps = sub.cuadrilla ? getCuadrillaComps(sub.cuadrilla) : [];
        const equipos = sub.equipo ?? [];
        return (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: tokens.colorNeutralForeground3, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ textAlign: 'left', padding: '3px 8px 3px 0', fontWeight: 600 }}>Recurso</th>
                <th style={{ textAlign: 'left', padding: '3px 8px', fontWeight: 600 }}>Unidad</th>
                <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 600 }}>Cantidad</th>
                <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 600 }}>% Desp.</th>
                <th style={{ textAlign: 'right', padding: '3px 8px', fontWeight: 600 }}>Precio</th>
                <th style={{ textAlign: 'right', padding: '3px 0', fontWeight: 600 }}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {comps.length > 0 && groupRow(`Mano de obra${sub.cuadrilla?.tipo_cuadrilla ? ` · ${sub.cuadrilla.tipo_cuadrilla}` : ''}`, 'g-mo')}
              {comps.map((c, i) => {
                const enriched = c as typeof c & { precio_unitario?: number | null; clave_catalogo?: string; descripcion_catalogo?: string; fuente?: string };
                const cant = Number(c.cantidad ?? 0);
                const precio = enriched.precio_unitario;
                return (
                  <tr key={`mo-${i}`} style={rowBorder(i)}>
                    <td style={tdName}>{c.categoria.replace(/_/g, ' ')}{origenNota(enriched)}</td>
                    <td style={tdUnit}>jornada</td>
                    <td style={tdR}>{cant}</td>
                    <td style={tdMuted}>—</td>
                    <td style={tdR}>{precioCell(precio)}</td>
                    <td style={{ ...tdR, padding: '4px 0' }}>{importeCell((precio ?? 0) * cant, precio != null)}</td>
                  </tr>
                );
              })}

              {sub.materiales.length > 0 && groupRow('Materiales', 'g-mat')}
              {sub.materiales.map((sm, i) => {
                const enriched = sm as typeof sm & { precio_unitario?: number | null; clave_catalogo?: string; descripcion_catalogo?: string; fuente?: string };
                const cant = Number(sm.cantidad_neta ?? 0);
                const desp = Number(sm.desperdicio_pct ?? 0);
                const precio = enriched.precio_unitario;
                return (
                  <tr key={`mat-${i}`} style={rowBorder(i)}>
                    <td style={tdName}>{sm.descripcion_normalizada}{origenNota(enriched)}</td>
                    <td style={tdUnit}>{sm.unidad || '—'}</td>
                    <td style={tdR}>{sm.cantidad_neta}</td>
                    <td style={tdMuted}>{desp ? `${desp}%` : '—'}</td>
                    <td style={tdR}>{precioCell(precio)}</td>
                    <td style={{ ...tdR, padding: '4px 0' }}>{importeCell((precio ?? 0) * cant * (1 + desp / 100), precio != null)}</td>
                  </tr>
                );
              })}

              {equipos.length > 0 && groupRow('Equipo', 'g-eq')}
              {equipos.map((e, i) => {
                const enriched = e as typeof e & { precio_unitario?: number | null; clave_catalogo?: string; descripcion_catalogo?: string; fuente?: string };
                // Dentro de una sub-receta el rendimiento es el del auxiliar. Solo lo
                // usan los renglones HEREDADOS (base por jornada).
                const horas = cantidadEquipoPorUnidad(e, Number(sub.rendimiento_base ?? 0)).cantidad;
                const precio = enriched.precio_unitario;
                return (
                  <tr key={`eq-${i}`} style={rowBorder(i)}>
                    <td style={tdName}>{e.descripcion_normalizada}{origenNota(enriched)}</td>
                    <td style={tdUnit}>hora</td>
                    <td style={tdR}>{horas}</td>
                    <td style={tdMuted}>—</td>
                    <td style={tdR}>{precioCell(precio)}</td>
                    <td style={{ ...tdR, padding: '4px 0' }}>{importeCell((precio ?? 0) * horas, precio != null)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      })()}
    </div>
  );
}

// =============================================================
// Helpers visuales
// =============================================================

function SideField({ label, value, styles }: { label: string; value: string | number; styles: ReturnType<typeof useStyles> }) {
  return (
    <div className={styles.sideField}>
      <div className={styles.sideFieldLabel}>{label}</div>
      <div className={styles.sideFieldValue}>{value}</div>
    </div>
  );
}

function InsumoRow({ insumo, styles }: { insumo: ApuIaInsumo; styles: ReturnType<typeof useStyles> }) {
  const isAuxiliar = insumo.tipo === 'auxiliar' && insumo.sub_receta;
  return (
    <>
      <div className={`${styles.dataTableRow} ${styles.gridMat} ${isAuxiliar ? styles.auxRow : ''}`}>
        <div>
          {isAuxiliar && <span style={{ marginRight: 6 }}>▼</span>}
          {insumo.descripcion_normalizada}
          {isAuxiliar && (
            <Badge size="extra-small" appearance="outline" color="brand" style={{ marginLeft: 6 }}>
              AUXILIAR
            </Badge>
          )}
        </div>
        <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{insumo.cantidad_neta}</div>
        <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{insumo.desperdicio_pct ?? 0}%</div>
        <div style={{ textAlign: 'center', color: tokens.colorNeutralForeground3 }}>{insumo.unidad}</div>
      </div>
      {isAuxiliar && insumo.sub_receta && (
        <div className={styles.auxDetail}>
          ↳ R: {insumo.sub_receta.rendimiento_base} {insumo.sub_receta.rendimiento_base_unidad}
          {insumo.sub_receta.cuadrilla && (
            <> · cuadrilla {insumo.sub_receta.cuadrilla.tipo_cuadrilla} ({getCuadrillaComps(insumo.sub_receta.cuadrilla).map((c: { cantidad: number; categoria: string }) => `${c.cantidad} ${c.categoria}`).join(' + ')})</>
          )}
          <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
            {insumo.sub_receta.materiales.map((sm, i) => (
              <li key={i}>{sm.descripcion_normalizada}: {sm.cantidad_neta} {sm.unidad}{sm.desperdicio_pct ? ` (+${sm.desperdicio_pct}% desp)` : ''}</li>
            ))}
            {(insumo.sub_receta.equipo ?? []).map((e, i) => (
              <li key={`e${i}`}><em>equipo:</em> {e.descripcion_normalizada} · {cantidadEquipoPorUnidad(e, Number(insumo.sub_receta!.rendimiento_base ?? 0)).cantidad} hr/unidad</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// =============================================================
// Bloque de las 7 validaciones canónicas (footer del análisis)
// =============================================================

function ValidacionesBlock({
  validaciones, alertas, preguntas, styles,
}: {
  validaciones: Array<{ id: string; tipo: 'success' | 'warning' | 'danger'; msg: string; critica?: boolean }>;
  alertas: string[];
  preguntas: string[];
  styles: ReturnType<typeof useStyles>;
}) {
  const [expanded, setExpanded] = useState(false);
  const danger = validaciones.filter((v) => v.tipo === 'danger').length;
  const warning = validaciones.filter((v) => v.tipo === 'warning').length;
  const success = validaciones.filter((v) => v.tipo === 'success').length;
  const total = validaciones.length;
  const visibles = expanded ? validaciones : validaciones.slice(0, 3);

  return (
    <div>
      <div className={styles.vLabel}>
        <span>Validaciones</span>
        <span>
          {danger > 0 && <span style={{ color: '#f87171', marginLeft: 4 }}>{danger} crítica{danger > 1 ? 's' : ''}</span>}
          {warning > 0 && <span style={{ color: 'var(--warn)', marginLeft: 4 }}>{warning} aviso{warning > 1 ? 's' : ''}</span>}
          {danger === 0 && warning === 0 && success > 0 && <span style={{ color: '#4ade80', marginLeft: 4 }}>✓ ok</span>}
        </span>
      </div>
      {visibles.length === 0 ? (
        <div className={`${styles.vItem} ${styles.vSuccess}`}>✓ Sin validaciones registradas</div>
      ) : (
        visibles.map((v) => (
          <div
            key={v.id}
            className={`${styles.vItem} ${v.tipo === 'success' ? styles.vSuccess : v.tipo === 'warning' ? styles.vWarning : styles.vDanger}`}
            title={v.id}
          >
            {v.tipo === 'success' ? '✓' : v.tipo === 'warning' ? '⚠' : '✕'} {v.msg}
          </div>
        ))
      )}
      {alertas.map((a, i) => (
        <div key={`a${i}`} className={`${styles.vItem} ${styles.vWarning}`}>⚠ {a}</div>
      ))}
      {preguntas.length > 0 && (
        <div className={`${styles.vItem} ${styles.vWarning}`}>? {preguntas.length} pregunta(s) pendiente(s)</div>
      )}
      {total > 3 && (
        <a
          style={{ fontSize: 11, marginTop: 4, display: 'inline-block', cursor: 'pointer', color: tokens.colorBrandForeground1 }}
          onClick={() => setExpanded((x) => !x)}
        >
          {expanded ? 'Ver menos' : `Ver las ${total} validaciones`}
        </a>
      )}
    </div>
  );
}

// =============================================================
// Filter chip (chips de filtro por confianza)
// =============================================================

function FilterChip({
  label, count, active, onClick, dotColor, icon,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  dotColor?: string;
  icon?: string;
}) {
  // Delega en el Chip compartido (estilo único de pill en toda la app).
  return (
    <Chip
      label={label}
      count={count}
      active={active}
      onClick={onClick}
      dotColor={dotColor}
      icon={icon ? <span style={{ fontSize: 12 }}>{icon}</span> : undefined}
    />
  );
}

